import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import SignatureCanvas from 'react-signature-canvas'
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import UserAvatar from 'components/Avatar'
import AssigneeAvatars from 'components/AssigneeAvatars'
import SearchableSelect from 'components/SearchableSelect'
import { RAG } from 'features/dashboard/charts/scoreColour'
import moment from 'moment'
import { SkeletonLine, SkeletonRows } from 'components/Skeleton'
import { toast } from 'react-toastify'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Tooltip from '@mui/material/Tooltip'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { cadenceLabel } from 'features/compliance/obligationConstants'
import CustomiseObligationModal from 'features/compliance/CustomiseObligationModal'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RepeatIcon from '@mui/icons-material/Repeat'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import LinkIcon from '@mui/icons-material/Link'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import HistoryIcon from '@mui/icons-material/History'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import CloseIcon from '@mui/icons-material/Close'
import useOnlineStatus from 'hooks/useOnlineStatus'
import useMediaQuery from 'hooks/useMediaQuery'
import FormRenderer from './components/FormRenderer'
import ScoreSummaryCard from './components/ScoreSummaryCard'
import FormResponseViewer from './components/FormResponseViewer'
import AssignFormPanel from './components/AssignFormPanel'
import TaskNextStep, { getNextStep } from './components/TaskNextStep'
import GenericFormTip from './components/GenerateFormFromDocument/GenericFormTip'
import { isGenericCompletionForm } from './components/GenerateFormFromDocument/formGeneration'
import ConfirmModal from 'features/portfolio/components/ConfirmModal'
import { approveConfirmOptions } from 'features/taskManagement/approveConfirm'
import TaskFormQuickEdit from './components/TaskFormQuickEdit'
import { recurrenceLabel } from './recurrenceLabel'
import ConflictResolutionPanel from './components/ConflictResolutionPanel'
import FormVersionHistory from './components/FormVersionHistory'
import UserPicker from './components/UserPicker'
import TaskActivityTimeline from './components/TaskActivityTimeline'
import TaskPendingActionsPanel from './components/TaskPendingActionsPanel'
import TaskRelatedDocuments from 'features/taskLinks/TaskRelatedDocuments'
import TaskOrigin from 'features/taskLinks/TaskOrigin'
import ImportedTaskDetails from 'features/taskManagement/ImportedTaskDetails'
import TaskReferenceFiles from './components/TaskReferenceFiles'
import TaskQrCodeModal from './components/TaskQrCodeModal'
import { listContractors } from 'services/contractorService'
import { fetchProgramme, fetchTaskDomains } from 'services/ProgrammeService'
import { getObligationSetup } from 'services/checklistService'
import { useSeriesScope } from 'features/taskManagement/useSeriesScope'
import QrCodeIcon from '@mui/icons-material/QrCode'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import {
  downloadTaskReport,
  getTask as getTaskRequest,
  getTaskOccurrences,
  stopRecurrence,
  previewAssignmentWarnings,
  sendTaskReminder,
  searchAssignableUsers,
} from 'services/taskService'
import { hideEmail } from 'utils/user'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined'
import FollowUpTaskModal from './components/FollowUpTaskModal'
import { getHubReturn } from 'utils/hubReturnPath'
import { taskBackTarget } from 'features/taskManagement/taskBackTarget'
import { maskEmail } from 'utils/string'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import {
  getTaskRoutine,
  createTaskRoutine,
  saveFormResponseRoutine,
  saveApproverFormResponseRoutine,
  saveAndSubmitTaskRoutine,
  approveTaskRoutine,
  rejectTaskRoutine,
  assignTaskRoutine,
  syncBlockersRoutine,
  getTasksRoutine,
  assignExternalUserRoutine,
  removeExternalAssigneeRoutine,
  updateApproversRoutine,
  updateTaskRoutine,
  getTeamsRoutine,
  getConflictsRoutine,
  getFormVersionsRoutine,
  replicateTaskRoutine,
  holdTaskRoutine,
  resumeTaskRoutine,
  signOffTaskRoutine
} from './ducks/actions'
import {
  selectCurrentTask,
  selectCurrentTaskLoading,
  selectCurrentTaskNotFound,
  selectFormResponseSaving,
  selectApproverFormSaving,
  selectTasks,
  selectTeams,
  selectConflicts,
  selectConflictsLoading,
  selectFormVersions,
  selectFormVersionsLoading
} from './ducks/selectors'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const STATUS_ICON = {
  unassigned: RadioButtonUncheckedIcon,
  assigned: GroupOutlinedIcon,
  pending_dependent: HourglassEmptyIcon,
  visited_awaiting_report: HourglassEmptyIcon,
  contractor_rescheduled: HourglassEmptyIcon,
  in_progress: PlayCircleOutlineIcon,
  on_hold: PauseCircleOutlineIcon,
  awaiting_approval: PendingActionsIcon,
  waiting_remedial: ErrorOutlineIcon,
  completed: TaskAltIcon,
  overdue: ErrorOutlineIcon
}

const priorityConfig = {
  low: { label: PRIORITY_META.low.label, color: PRIORITY_META.low.fg, bg: PRIORITY_META.low.bg },
  medium: { label: PRIORITY_META.medium.label, color: PRIORITY_META.medium.fg, bg: PRIORITY_META.medium.bg },
  high: { label: PRIORITY_META.high.label, color: PRIORITY_META.high.fg, bg: PRIORITY_META.high.bg },
  critical: { label: PRIORITY_META.critical.label, color: PRIORITY_META.critical.fg, bg: PRIORITY_META.critical.bg }
}

const PRIORITY_RAG = {
  low:    RAG.low.fg,
  medium: RAG.medium.fg,
  high:   RAG.high.fg,
  critical: RAG.critical.fg,
}

// Submission location is captured automatically on every save/submit.
// Shown above the form area so the audit lineage of "who filled it,
// where, when" is visible at a glance. Denied/unavailable states render
// as a soft note instead of hiding — the absence of a fix is itself
// information for compliance teams.
const SubmissionLocationBanner = ({ location }) => {
  if (!location || !location.source) return null
  const when = location.capturedAt || location.captured_at
  const dateText = when ? new Date(when).toLocaleString() : null
  const accuracy = location.accuracyM ?? location.accuracy_m
  const lat = typeof location.lat === 'number' ? location.lat : null
  const lng = typeof location.lng === 'number' ? location.lng : null

  let body = null
  let tone = '#0e7490'
  let toneBg = '#ecfeff'
  if (location.source === 'browser' && lat != null && lng != null) {
    body = (
      <>
        <strong>Captured at</strong> {lat.toFixed(5)}°, {lng.toFixed(5)}°
        {accuracy != null && <> · ±{accuracy} m</>}
        {dateText && <> · {dateText}</>}
      </>
    )
  } else if (location.source === 'denied') {
    tone = '#92400e'; toneBg = '#fef3c7'
    body = <>Location permission denied{dateText && <> · last save {dateText}</>}</>
  } else if (location.source === 'declined') {
    tone = '#92400e'; toneBg = '#fef3c7'
    body = <>Location sharing declined{dateText && <> · last save {dateText}</>}</>
  } else {
    tone = '#475569'; toneBg = '#f1f5f9'
    body = <>Location unavailable{dateText && <> · last save {dateText}</>}</>
  }

  return (
    <SubmissionLocBanner $tone={tone} $bg={toneBg}>
      <RoomOutlinedIcon style={{ fontSize: 14 }} />
      <span>{body}</span>
    </SubmissionLocBanner>
  )
}

const TaskDetail = () => {
  const { id } = useParams()
  const location = useLocation()
  const reactRouterNavigate = useNavigate()
  const dispatch = useDispatch()
  const task = useSelector(selectCurrentTask)
  const currentUser = useSelector(getCurrentUser)
  const isLoading = useSelector(selectCurrentTaskLoading)
  const currentTaskNotFound = useSelector(selectCurrentTaskNotFound)
  const formResponseSaving = useSelector(selectFormResponseSaving)
  const approverFormSaving = useSelector(selectApproverFormSaving)
  const allTasks = useSelector(selectTasks)
  const conflicts = useSelector(selectConflicts)
  const conflictsLoading = useSelector(selectConflictsLoading)
  const formVersions = useSelector(selectFormVersions)
  const formVersionsLoading = useSelector(selectFormVersionsLoading)
  const teams = useSelector(selectTeams)
  const [approverEditing, setApproverEditing] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [extQr, setExtQr] = useState(null)
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)
  const [activityTimelineOpen, setActivityTimelineOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [confirmState, setConfirmState] = useState(null)
  const [formProgress, setFormProgress] = useState(null)
  const [submitBlockedMessage, setSubmitBlockedMessage] = useState(null)
  const formRendererRef = useRef(null)
  const assignFormPanelRef = useRef(null)
  const confirmResolveRef = useRef(null)
  const askConfirm = useCallback(
    options => new Promise(resolve => {
      confirmResolveRef.current = resolve
      setConfirmState(options)
    }),
    []
  )
  const closeConfirm = useCallback(result => {
    const resolve = confirmResolveRef.current
    confirmResolveRef.current = null
    setConfirmState(null)
    if (resolve) resolve(result)
  }, [])
  const [customiseObl, setCustomiseObl] = useState(null)
  const [customiseCtx, setCustomiseCtx] = useState({ tags: [], domains: [], bpName: null })
  const [customiseLoading, setCustomiseLoading] = useState(false)
  const [occurrences, setOccurrences] = useState([])
  const [occurrenceSchedule, setOccurrenceSchedule] = useState(null)
  const [occurrencesLoading, setOccurrencesLoading] = useState(false)
  const { promptNode: seriesScopePrompt, requestScope: requestSeriesScope } = useSeriesScope(task)

  const occurrencesTaskIdRef = useRef(null)
  const occurrencesSignature = [
    task?.status,
    task?.deadline,
    task?.completedAt,
    (task?.assignees || []).map(a => a.id).join(','),
    (task?.externalAssignees || []).map(e => e.id).join(',')
  ].join('|')

  useEffect(() => {
    const isSeries = !!(task?.complianceObligation || task?.recurrenceType === 'repeating' || task?.parentTaskId)
    if (!task?.id || !isSeries) { setOccurrences([]); setOccurrenceSchedule(null); occurrencesTaskIdRef.current = null; return }
    const showSpinner = occurrencesTaskIdRef.current !== task.id
    occurrencesTaskIdRef.current = task.id
    let cancelled = false
    if (showSpinner) setOccurrencesLoading(true)
    getTaskOccurrences(task.id)
      .then(res => {
        if (cancelled) return
        setOccurrences(res?.data?.data?.occurrences || [])
        setOccurrenceSchedule(res?.data?.data?.schedule || null)
      })
      .catch(() => { if (!cancelled && showSpinner) { setOccurrences([]); setOccurrenceSchedule(null) } })
      .finally(() => { if (!cancelled && showSpinner) setOccurrencesLoading(false) })
    return () => { cancelled = true }
  }, [task?.id, task?.complianceObligation?.id, task?.recurrenceType, task?.parentTaskId, occurrencesSignature])
  const [rejectReason, setRejectReason] = useState('')
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [assigneeIds, setAssigneeIds] = useState([])
  const [assignablePeople, setAssignablePeople] = useState([])
  const [assignableSearch, setAssignableSearch] = useState('')
  const [loadingAssignable, setLoadingAssignable] = useState(false)
  const [remindModalOpen, setRemindModalOpen] = useState(false)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false)
  const [remindIds, setRemindIds] = useState([])
  const [remindNote, setRemindNote] = useState('')
  const [remindSending, setRemindSending] = useState(false)
  // Cert mismatch warnings for the current assigneeIds preview. Always
  // advisory — never blocks Save Assignees per product direction.
  const [certWarnings, setCertWarnings] = useState([])
  const [requiredCerts, setRequiredCerts] = useState([])

  // Re-fetch advisory cert warnings whenever the picker selection
  // changes — debounced via a 200ms timer because UserPicker emits a
  // change for every keystroke that mutates the list.
  useEffect(() => {
    if (!assignModalOpen || !task?.id) return
    const ids = (assigneeIds || []).filter(Boolean)
    const timer = setTimeout(() => {
      previewAssignmentWarnings({ taskId: task.id, assigneeIds: ids })
        .then(res => {
          setCertWarnings(res?.data?.data?.warnings || [])
          setRequiredCerts(res?.data?.data?.requiredCertifications || [])
        })
        .catch(() => { setCertWarnings([]) })
    }, 200)
    return () => clearTimeout(timer)
  }, [assignModalOpen, task?.id, assigneeIds])
  const [approverModalOpen, setApproverModalOpen] = useState(false)
  const [approverIds, setApproverIds] = useState([])
  const [enableApprovalAfterSave, setEnableApprovalAfterSave] = useState(false)
  const [blockersModalOpen, setBlockersModalOpen] = useState(false)
  const [selectedBlockerIds, setSelectedBlockerIds] = useState([])
  const [externalModalOpen, setExternalModalOpen] = useState(false)
  const [externalEmail, setExternalEmail] = useState('')
  const [externalName, setExternalName] = useState('')
  const [externalExpiry, setExternalExpiry] = useState('')
  const [teamModalOpen, setTeamModalOpen] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editDescription, setEditDescription] = useState('')
  const [isEditingDeadline, setIsEditingDeadline] = useState(false)
  const [editDeadline, setEditDeadline] = useState('')
  const [isEditingPriority, setIsEditingPriority] = useState(false)
  const [editPriority, setEditPriority] = useState('')
  const [isEditingContractor, setIsEditingContractor] = useState(false)
  const [availableContractors, setAvailableContractors] = useState([])
  const [isEditingDomain, setIsEditingDomain] = useState(false)
  const [availableDomains, setAvailableDomains] = useState([])
  const [isEditingObligation, setIsEditingObligation] = useState(false)
  const [obligationOptions, setObligationOptions] = useState(null)
  const [isEditingPoNumber, setIsEditingPoNumber] = useState(false)
  const [editPoNumber, setEditPoNumber] = useState('')
  const [stoppingRecurrence, setStoppingRecurrence] = useState(false)
  const [isEditingRecurrence, setIsEditingRecurrence] = useState(false)
  const [editRecType, setEditRecType] = useState('one_off')
  const [editRecInterval, setEditRecInterval] = useState(1)
  const [editRecUnit, setEditRecUnit] = useState('month')
  const [editRecStart, setEditRecStart] = useState('')
  const [editRecEnd, setEditRecEnd] = useState('')
  const [holdModalOpen, setHoldModalOpen] = useState(false)
  const [holdReason, setHoldReason] = useState('')
  const [holdNextAction, setHoldNextAction] = useState('')
  const [signOffModalOpen, setSignOffModalOpen] = useState(false)
  const [signOffNotes, setSignOffNotes] = useState('')
  const [signOffSignature, setSignOffSignature] = useState(null)
  const signOffSigRef = useRef(null)
  const titleInputRef = useRef(null)
  const descriptionRef = useRef(null)
  const deadlineRef = useRef(null)
  const pageWrapperRef = useRef(null)
  const lastScrollTopRef = useRef(0)
  const [mobileActionBarVisible, setMobileActionBarVisible] = useState(true)
  const isNarrowHeader = useMediaQuery('(max-width: 640px)')
  const [actionBarEl, setActionBarEl] = useState(null)

  useEffect(() => {
    const root = document.documentElement
    if (!isNarrowHeader || !actionBarEl || !mobileActionBarVisible) {
      root.style.removeProperty('--bp-page-bar')
      return undefined
    }
    const apply = () => {
      root.style.setProperty('--bp-page-bar', `${Math.ceil(actionBarEl.getBoundingClientRect().height)}px`)
    }
    apply()
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null
    if (observer) observer.observe(actionBarEl)
    return () => {
      if (observer) observer.disconnect()
      root.style.removeProperty('--bp-page-bar')
    }
  }, [isNarrowHeader, actionBarEl, mobileActionBarVisible])
  const isOnline = useOnlineStatus()
  const prevOnlineRef = useRef(isOnline)
  const formAreaRef = useRef(null)
  const scrolledToFormRef = useRef(false)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get('openForm') !== '1' || !task || scrolledToFormRef.current) return
    const timer = setTimeout(() => {
      if (formAreaRef.current) {
        formAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        scrolledToFormRef.current = true
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchParams, task])

  useEffect(() => {
    scrolledToFormRef.current = false
  }, [id])

  useEffect(() => {
    if (id) {
      dispatch(getTaskRoutine(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (
      task &&
      task.status === 'awaiting_approval' &&
      task.permissions?.canApprove
    ) {
      dispatch(getConflictsRoutine(task.id))
    }
  }, [dispatch, task?.id, task?.status, task?.permissions?.canApprove])

  useEffect(() => {
    prevOnlineRef.current = isOnline
  }, [isOnline])

  useEffect(() => {
    if (teams.length === 0) {
      dispatch(getTeamsRoutine())
    }
  }, [dispatch, teams.length])

  useEffect(() => {
    listContractors()
      .then(res => setAvailableContractors(res?.data?.data?.contractors || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetchTaskDomains()
      .then(res => setAvailableDomains(res?.data?.data || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (window.innerWidth > 640) return undefined

    const html = document.documentElement
    const body = document.body
    const prevHtmlOverscroll = html.style.overscrollBehaviorY
    const prevBodyOverscroll = body.style.overscrollBehaviorY
    const prevBodyOverflow = body.style.overflow

    html.style.overscrollBehaviorY = 'none'
    body.style.overscrollBehaviorY = 'none'
    body.style.overflow = 'hidden'

    return () => {
      html.style.overscrollBehaviorY = prevHtmlOverscroll
      body.style.overscrollBehaviorY = prevBodyOverscroll
      body.style.overflow = prevBodyOverflow
    }
  }, [])

  // Auto-enter title edit mode when redirected from replicate
  useEffect(() => {
    if (task && !isLoading && location.search.includes('editTitle=true')) {
      setEditTitle(task.title)
      setIsEditingTitle(true)
      // Clean up the URL without triggering navigation
      window.history.replaceState({}, '', location.pathname)
    }
  }, [task?.id, isLoading])

  // Focus and select title input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  const selectedTeamData = useMemo(() => {
    if (!selectedTeamId) return null
    return teams.find(t => t.id === selectedTeamId) || null
  }, [teams, selectedTeamId])

  const taskTeamData = useMemo(() => {
    if (!task?.team?.id) return null
    return teams.find(t => t.id === task.team.id) || null
  }, [teams, task?.team?.id])

  const teamInternalMembers = useMemo(() => {
    if (!taskTeamData?.members) return []
    return taskTeamData.members.filter(m => m.user && !m.isPending)
  }, [taskTeamData])

  const teamExternalMembers = useMemo(() => {
    if (!taskTeamData?.members) return []
    return taskTeamData.members.filter(m => !m.user || m.isPending)
  }, [taskTeamData])

  const handleOpenTeamModal = () => {
    setSelectedTeamId(task.team?.id || '')
    setTeamModalOpen(true)
  }

  const handleSaveTeam = async () => {
    const scope = await requestSeriesScope()
    if (scope === null) return
    dispatch(
      updateTaskRoutine({ id: task.id, taskTeamId: selectedTeamId || null, scope })
    )
    setTeamModalOpen(false)
  }

  const handleAddSingleInternalAssignee = userId => {
    const currentIds = task.assignees?.map(a => a.id) || []
    if (!currentIds.includes(userId)) {
      dispatch(
        assignTaskRoutine({
          taskId: task.id,
          assigneeIds: [...currentIds, userId]
        })
      )
    }
  }

  const handleAddInternalAsAssignees = () => {
    const currentIds = task.assignees?.map(a => a.id) || []
    const newIds = teamInternalMembers
      .map(m => m.user.id)
      .filter(id => !currentIds.includes(id))
    if (newIds.length > 0) {
      dispatch(
        assignTaskRoutine({
          taskId: task.id,
          assigneeIds: [...currentIds, ...newIds]
        })
      )
    }
  }

  const handleInviteExternalTeamMember = (email, name) => {
    const alreadyInvited = task.externalAssignees?.some(e => e.email === email)
    if (!alreadyInvited) {
      dispatch(
        assignExternalUserRoutine({
          taskId: task.id,
          email,
          name: name || null,
          expiresAt: null
        })
      )
    }
  }

  const autoPopulateKeys = useMemo(() => {
    const map = task?.formTemplate?.autoPopulateMap
    if (!map) return []
    const toSnakeCase = str => str.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`)
    return Object.keys(map).map(toSnakeCase)
  }, [task?.formTemplate?.autoPopulateMap])

  const handleAutoSaveForm = useCallback(
    (responses, meta = {}) => {
      dispatch(
        saveFormResponseRoutine({
          taskId: task.id,
          responses,
          autoSave: true,
          currentVersion: task.formResponse?.currentVersion,
          submissionLocation: meta.submissionLocation || null,
        })
      )
      setLastSavedAt(new Date().toISOString())
    },
    [dispatch, task?.id, task?.formResponse?.currentVersion]
  )

  const trackerReturn = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const bpId = params.get('bpId')
    return params.get('from') === 'programme' && bpId ? `/bp-list/${bpId}/programme` : null
  }, [location.search])

  const backLabel = taskBackTarget(location, trackerReturn, `${PRIVATE_PATHS.taskManagement}?tab=tasks`).label

  const handleBack = () => {
    if (task?.id) dispatch(getTaskRoutine(task.id))
    // Prefer the browser's own back when there's somewhere safe to go —
    // it restores the exact previous URL (including any URL-based
    // filters/search/scroll) without the round-trip through a custom
    // store. Only fall back to the stashed list URL when the user
    // landed here cold (direct link, new tab, bookmark).
    const target = taskBackTarget(location, trackerReturn, getHubReturn(`${PRIVATE_PATHS.taskManagement}?tab=tasks`))
    if (target.back) {
      reactRouterNavigate(-1)
      return
    }
    if (target.inApp) {
      reactRouterNavigate(target.path)
      return
    }
    redirect(target.path)
  }

  const handleSaveForm = (responses, meta = {}) => {
    dispatch(
      saveFormResponseRoutine({
        taskId: task.id,
        responses,
        currentVersion: task.formResponse?.currentVersion,
        submissionLocation: meta.submissionLocation || null,
      })
    )
    setLastSavedAt(new Date().toISOString())
  }

  const handleApproverSaveForm = (responses, meta = {}) => {
    dispatch(
      saveApproverFormResponseRoutine({
        taskId: task.id,
        responses,
        currentVersion: task.formResponse?.currentVersion,
        submissionLocation: meta.submissionLocation || null,
      })
    )
  }

  const handleSubmitForm = (responses, meta = {}) => {
    dispatch(
      saveAndSubmitTaskRoutine({
        taskId: task.id,
        responses,
        currentVersion: task.formResponse?.currentVersion,
        submissionLocation: meta.submissionLocation || null,
      })
    )
  }

  const handleRefreshTask = useCallback(async () => {
    if (!task?.id) return null

    dispatch(getTaskRoutine.request())

    try {
      const result = await getTaskRequest(task.id)
      const refreshedTask = result.data?.data || null
      dispatch(getTaskRoutine.success(refreshedTask))
      return refreshedTask
    } catch (error) {
      dispatch(getTaskRoutine.failure(error))
      throw error
    }
  }, [dispatch, task?.id])

  const handleToggleVersionHistory = () => {
    if (!versionHistoryOpen && formVersions.length === 0) {
      dispatch(getFormVersionsRoutine(task.id))
    }
    setVersionHistoryOpen(!versionHistoryOpen)
  }

  const handleApprove = async () => {
    const confirmed = await askConfirm(approveConfirmOptions(task))
    if (confirmed) dispatch(approveTaskRoutine(task.id))
  }

  const handleReject = () => {
    setRejectModalOpen(true)
  }

  const handleRejectConfirm = () => {
    if (rejectReason.trim()) {
      dispatch(rejectTaskRoutine({ id: task.id, reason: rejectReason.trim() }))
      setRejectModalOpen(false)
      setRejectReason('')
    }
  }

  const handleHold = () => {
    setHoldModalOpen(true)
  }

  const handleHoldConfirm = () => {
    if (holdReason.trim()) {
      dispatch(
        holdTaskRoutine({
          id: task.id,
          reason: holdReason.trim(),
          nextAction: holdNextAction.trim() || undefined
        })
      )
      setHoldModalOpen(false)
      setHoldReason('')
      setHoldNextAction('')
    }
  }

  const handleResume = async () => {
    const confirmed = await askConfirm({
      title: 'Resume this task?',
      message: 'It will come off hold so work can carry on.',
      confirmLabel: 'Resume',
      tone: 'primary'
    })
    if (confirmed) dispatch(resumeTaskRoutine(task.id))
  }

  const handleSignOff = () => {
    setSignOffNotes('')
    setSignOffSignature(null)
    setSignOffModalOpen(true)
  }

  const handleSignOffConfirm = () => {
    const sigData =
      signOffSigRef.current && !signOffSigRef.current.isEmpty()
        ? signOffSigRef.current.toDataURL('image/png')
        : undefined
    dispatch(
      signOffTaskRoutine({
        id: task.id,
        notes: signOffNotes.trim() || undefined,
        signature: sigData
      })
    )
    setSignOffModalOpen(false)
    setSignOffNotes('')
    setSignOffSignature(null)
  }

  const handleClearSignOffSignature = () => {
    if (signOffSigRef.current) {
      signOffSigRef.current.clear()
    }
    setSignOffSignature(null)
  }

  const handleOpenRemindModal = () => {
    setRemindIds((task?.assignees || []).map(a => a.id))
    setRemindNote('')
    setRemindModalOpen(true)
  }

  const handleSendReminder = async () => {
    if (remindIds.length === 0) {
      toast.error('Choose at least one person to remind.')
      return
    }
    setRemindSending(true)
    try {
      const res = await sendTaskReminder({ taskId: task.id, recipientIds: remindIds, note: remindNote.trim() || undefined })
      toast.success(res?.data?.message || 'Reminder sent.')
      setRemindModalOpen(false)
    } catch (e) {
      toast.error('Could not send the reminder.')
    } finally {
      setRemindSending(false)
    }
  }

  const handleOpenAssignModal = () => {
    setAssigneeIds(task.assignees?.map(a => a.id) || [])
    setAssignableSearch('')
    setAssignModalOpen(true)
  }

  const handleSaveAssignees = async () => {
    const scope = await requestSeriesScope()
    if (scope === null) return
    dispatch(assignTaskRoutine({ taskId: task.id, assigneeIds, scope }))
    setAssignModalOpen(false)
  }

  const handleAddAssignees = async () => {
    const scope = await requestSeriesScope()
    if (scope === null) return
    const originalIds = task.assignees?.map(a => a.id) || []
    const merged = Array.from(new Set([...originalIds, ...assigneeIds]))
    dispatch(assignTaskRoutine({ taskId: task.id, assigneeIds: merged, scope }))
    setAssignModalOpen(false)
  }

  const toggleAssignee = id =>
    setAssigneeIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

  const assignPeopleList = useMemo(() => {
    const current = task?.assignees || []
    const seen = new Set(current.map(a => a.id))
    const merged = [...current, ...(assignablePeople || []).filter(u => !seen.has(u.id))]
    const q = assignableSearch.trim().toLowerCase()
    if (!q) return merged
    return merged.filter(u =>
      [`${u.firstName || ''} ${u.lastName || ''}`, u.email]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    )
  }, [task?.assignees, assignablePeople, assignableSearch])

  const allAssigneesTicked =
    assignPeopleList.length > 0 && assignPeopleList.every(u => assigneeIds.includes(u.id))

  const toggleSelectAllAssignees = () => {
    const visibleIds = assignPeopleList.map(u => u.id)
    if (allAssigneesTicked) {
      const visible = new Set(visibleIds)
      setAssigneeIds(prev => prev.filter(id => !visible.has(id)))
    } else {
      setAssigneeIds(prev => Array.from(new Set([...prev, ...visibleIds])))
    }
  }

  useEffect(() => {
    if (!assignModalOpen) return
    let live = true
    setLoadingAssignable(true)
    const timer = setTimeout(() => {
      searchAssignableUsers({ search: assignableSearch.trim(), buildingPassportId: task?.building?.id })
        .then(res => { if (live) setAssignablePeople(res?.data?.data || []) })
        .catch(() => { if (live) setAssignablePeople([]) })
        .finally(() => { if (live) setLoadingAssignable(false) })
    }, 250)
    return () => { live = false; clearTimeout(timer) }
  }, [assignModalOpen, assignableSearch, task?.building?.id])

  const handleOpenApproverModal = () => {
    setEnableApprovalAfterSave(false)
    setApproverIds(task.approvers?.map(a => a.id) || [])
    setApproverModalOpen(true)
  }

  const handleSaveApprovers = async () => {
    const scope = await requestSeriesScope()
    if (scope === null) return
    dispatch(updateApproversRoutine({ taskId: task.id, approverIds, scope }))
    setApproverModalOpen(false)
    if (approverIds.length === 0) setEnableApprovalAfterSave(false)
  }

  const handleToggleApproval = () => {
    const turningOn = !task.requiresApproval
    if (turningOn && !(task.approvers?.length > 0)) {
      setEnableApprovalAfterSave(true)
      setApproverIds(task.approvers?.map(a => a.id) || [])
      setApproverModalOpen(true)
      toast.info('Add an approver to require sign-off. A task set to "Approval required" needs someone to approve it.')
      return
    }
    dispatch(updateTaskRoutine({ id: task.id, requiresApproval: turningOn }))
  }

  const dismissApproverModal = () => {
    setApproverModalOpen(false)
    setEnableApprovalAfterSave(false)
  }

  useEffect(() => {
    if (enableApprovalAfterSave && task?.approvers?.length > 0 && !task?.requiresApproval) {
      dispatch(updateTaskRoutine({ id: task.id, requiresApproval: true }))
      setEnableApprovalAfterSave(false)
    }
  }, [enableApprovalAfterSave, task?.approvers?.length, task?.requiresApproval, task?.id, dispatch])

  const handleOpenBlockersModal = () => {
    setSelectedBlockerIds(task.blockedBy?.map(b => b.id) || [])
    if (allTasks.length === 0) {
      dispatch(getTasksRoutine({ page: 1 }))
    }
    setBlockersModalOpen(true)
  }

  const handleSaveBlockers = () => {
    dispatch(
      syncBlockersRoutine({ taskId: task.id, blockerIds: selectedBlockerIds })
    )
    setBlockersModalOpen(false)
  }

  const toggleBlocker = blockerId => {
    setSelectedBlockerIds(prev =>
      prev.includes(blockerId)
        ? prev.filter(id => id !== blockerId)
        : [...prev, blockerId]
    )
  }

  const handleOpenExternalModal = () => {
    setExternalEmail('')
    setExternalName('')
    setExternalExpiry('')
    setExternalModalOpen(true)
  }

  const handleInviteExternal = async () => {
    if (externalEmail.trim()) {
      const scope = await requestSeriesScope()
      if (scope === null) return
      dispatch(
        assignExternalUserRoutine({
          taskId: task.id,
          email: externalEmail.trim(),
          name: externalName.trim() || null,
          expiresAt: externalExpiry || null,
          scope
        })
      )
      setExternalModalOpen(false)
      setExternalEmail('')
      setExternalName('')
      setExternalExpiry('')
    }
  }

  const handleRemoveExternal = async externalAssigneeId => {
    const confirmed = await askConfirm({
      title: 'Remove this external user?',
      message: 'They will no longer be able to open or complete this task.',
      confirmLabel: 'Remove'
    })
    if (confirmed) {
      const scope = await requestSeriesScope()
      if (scope === null) return
      dispatch(
        removeExternalAssigneeRoutine({
          taskId: task.id,
          externalAssigneeId,
          scope
        })
      )
    }
  }

  const handleOpenBuildingPassport = () => {
    if (task.building?.id) {
      window.open(`/bp-list/${task.building.id}`, '_blank')
    }
  }

  const handleReplicateAsDraft = () => {
    dispatch(replicateTaskRoutine(task.id))
  }

  const [reportDownloading, setReportDownloading] = useState(false)

  const completedReport = useMemo(() => {
    if (!task?.reports) return null
    return task.reports.find(r => r.status === 'completed') || null
  }, [task?.reports])

  const handleDownloadReport = async () => {
    if (!completedReport || !task.building?.id) return
    setReportDownloading(true)
    try {
      const res = await downloadTaskReport({
        buildingPassportId: task.building.id,
        reportId: completedReport.id
      })
      const url = res.data?.downloadUrl || res.data?.data?.downloadUrl
      if (url) {
        window.open(url, '_blank')
      }
    } catch (err) {
      console.error('Failed to download report:', err)
    } finally {
      setReportDownloading(false)
    }
  }

  const handleStartEditTitle = () => {
    setEditTitle(task.title)
    setIsEditingTitle(true)
  }

  const titleSavingRef = useRef(false)
  const handleSaveTitle = () => {
    if (titleSavingRef.current) return
    titleSavingRef.current = true
    const trimmed = editTitle.trim()
    if (trimmed && trimmed !== task.title) {
      dispatch(updateTaskRoutine({ id: task.id, title: trimmed }))
    }
    setIsEditingTitle(false)
    titleSavingRef.current = false
  }

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false)
  }

  // --- Inline editing: Description ---
  const handleStartEditDescription = () => {
    setEditDescription(task.description || '')
    setIsEditingDescription(true)
    setTimeout(() => descriptionRef.current?.focus(), 50)
  }

  const handleSaveDescription = () => {
    const trimmed = editDescription.trim()
    if (trimmed !== (task.description || '')) {
      dispatch(updateTaskRoutine({ id: task.id, description: trimmed || null }))
    }
    setIsEditingDescription(false)
  }

  // --- Inline editing: Deadline ---
  const handleStartEditDeadline = () => {
    if (obligationLocked) return
    setEditDeadline(
      task.deadline ? moment(task.deadline).format('YYYY-MM-DD') : ''
    )
    setIsEditingDeadline(true)
    setTimeout(() => deadlineRef.current?.focus(), 50)
  }

  const handleSaveDeadline = () => {
    if (obligationLocked) { setIsEditingDeadline(false); return }
    const currentVal = task.deadline
      ? moment(task.deadline).format('YYYY-MM-DD')
      : ''
    if (editDeadline !== currentVal) {
      dispatch(
        updateTaskRoutine({ id: task.id, deadline: editDeadline || null })
      )
    }
    setIsEditingDeadline(false)
  }

  // --- Inline editing: Priority ---
  const handleStartEditPriority = () => {
    setEditPriority(task.priority || '')
    setIsEditingPriority(true)
  }

  const handleSavePriority = value => {
    if (value !== (task.priority || '')) {
      dispatch(updateTaskRoutine({ id: task.id, priority: value || null }))
    }
    setIsEditingPriority(false)
  }

  // --- Task type inline edit ---
  const [isEditingTaskType, setIsEditingTaskType] = useState(false)
  const handleStartEditTaskType = () => setIsEditingTaskType(true)
  const handleSaveTaskType = (value) => {
    if (value !== (task.taskType || '')) {
      dispatch(updateTaskRoutine({ id: task.id, taskType: value || null }))
    }
    setIsEditingTaskType(false)
  }

  const handleStartEditContractor = () => setIsEditingContractor(true)
  const handleSaveContractor = async (value) => {
    if (value !== (task.contractorId || '')) {
      const scope = await requestSeriesScope()
      if (scope === null) { setIsEditingContractor(false); return }
      dispatch(updateTaskRoutine({ id: task.id, contractorId: value || null, scope }))
    }
    setIsEditingContractor(false)
  }
  const contractorName =
    task?.contractor?.name ||
    availableContractors.find(c => c.id === task?.contractorId)?.name ||
    ''

  const handleStartEditPoNumber = () => {
    setEditPoNumber(task?.poNumber || '')
    setIsEditingPoNumber(true)
  }
  const handleSavePoNumber = () => {
    const next = editPoNumber.trim()
    if (next !== (task.poNumber || '')) {
      dispatch(updateTaskRoutine({ id: task.id, poNumber: next || null }))
    }
    setIsEditingPoNumber(false)
  }

  const handleStartEditDomain = () => { if (!obligationLocked) setIsEditingDomain(true) }
  const handleSaveDomain = (value) => {
    if (obligationLocked) { setIsEditingDomain(false); return }
    if ((value || '') !== (task.taskDomainId || '')) {
      dispatch(updateTaskRoutine({ id: task.id, taskDomainId: value || null }))
    }
    setIsEditingDomain(false)
  }
  const domainOptions = useMemo(() => {
    const selectedId = task?.taskDomainId
    return availableDomains.filter(d => d.isActive || d.id === selectedId)
  }, [availableDomains, task?.taskDomainId])

  const handleCustomiseObligation = async () => {
    if (customiseLoading) return
    const bpId = task?.complianceObligation?.bpId || task?.building?.id
    const oblId = task?.complianceObligation?.id
    if (!bpId || !oblId) return
    setCustomiseLoading(true)
    try {
      const res = await getObligationSetup(bpId)
      const payload = res?.data?.data || {}
      const row = (payload.rows || []).find(r => r.id === oblId)
      if (!row) {
        toast.error('Could not find this obligation on the compliance tracker.')
        return
      }
      setCustomiseCtx({
        tags: (payload.tags || []).flatMap(g => (g.tags || []).map(t => ({ ...t, layer: g.layer }))),
        domains: payload.domains || [],
        bpName: task?.building?.name || null,
      })
      setCustomiseObl(row)
    } catch (e) {
      toast.error('Could not open the customise window. Please try again.')
    } finally {
      setCustomiseLoading(false)
    }
  }

  const handleStartEditObligation = () => {
    const buildingId = task?.building?.id
    if (!buildingId) return
    setIsEditingObligation(true)
    if (obligationOptions === null) {
      const bpId = buildingId
      fetchProgramme(bpId)
        .then(res => {
          const domains = res?.data?.data?.domains || []
          const options = []
          domains.forEach(d => {
            ;(d.obligations || []).forEach(o => {
              if (o.obligationKind === 'task' && !o.isSkipped) {
                options.push({ id: o.id, name: o.name, domainName: d.name })
              }
            })
          })
          setObligationOptions(options)
        })
        .catch(() => setObligationOptions([]))
    }
  }

  const handleSaveObligation = value => {
    if ((value || '') !== (task.complianceObligation?.id || '')) {
      const wasRepeating = task.recurrenceType === 'repeating'
      dispatch(
        updateTaskRoutine({ id: task.id, programmeObligationId: value || null })
      )
      if (value) {
        toast.info(
          wasRepeating
            ? 'Linked to the obligation. This task now follows the obligation’s schedule, so its own repeat setting was removed, and the compliance programme creates the next one automatically each cycle.'
            : 'Linked to the obligation. This task now follows the obligation’s schedule. The compliance programme creates the next one automatically each cycle.',
          { autoClose: 6000 }
        )
      }
    }
    setIsEditingObligation(false)
  }

  const obligationSelectOptions = useMemo(() => {
    const options = obligationOptions || []
    const current = task?.complianceObligation
    if (current?.id && !options.some(o => o.id === current.id)) {
      return [{ id: current.id, name: current.name, domainName: null }, ...options]
    }
    return options
  }, [obligationOptions, task?.complianceObligation])

  // --- Estimated duration inline edit ---
  const [isEditingDuration, setIsEditingDuration] = useState(false)
  const [editDuration, setEditDuration] = useState('')
  const handleStartEditDuration = () => {
    setEditDuration(task.estimatedDurationMinutes != null ? String(task.estimatedDurationMinutes) : '')
    setIsEditingDuration(true)
  }
  const handleSaveDuration = () => {
    const trimmed = editDuration.trim()
    const next = trimmed === '' ? null : Math.max(1, Math.min(100000, Number(trimmed) || 0))
    if (next !== task.estimatedDurationMinutes) {
      dispatch(updateTaskRoutine({ id: task.id, estimatedDurationMinutes: next }))
    }
    setIsEditingDuration(false)
  }

  // --- Assign to me ---
  const handleAssignToMe = () => {
    if (!currentUser?.id) return
    const currentIds = task.assignees?.map(a => a.id) || []
    if (!currentIds.includes(currentUser.id)) {
      dispatch(
        assignTaskRoutine({
          taskId: task.id,
          assigneeIds: [...currentIds, currentUser.id]
        })
      )
    }
  }

  const handleAddMeAsApprover = async () => {
    if (!currentUser?.id) return
    const currentIds = task.approvers?.map(a => a.id) || []
    if (currentIds.includes(currentUser.id)) return
    const isSelfAssignee = task.assignees?.some(a => a.id === currentUser.id)
    if (isSelfAssignee && !(await askConfirm({
      title: 'Approve your own work?',
      message: 'You are assigned to do this task, so approving it yourself means signing off your own work. Add yourself as approver anyway?',
      confirmLabel: 'Add me as approver',
      tone: 'primary'
    }))) {
      return
    }
    const scope = await requestSeriesScope()
    if (scope === null) return
    dispatch(
      updateApproversRoutine({
        taskId: task.id,
        approverIds: [...currentIds, currentUser.id],
        scope
      })
    )
  }

  // --- Stop recurrence ---
  const handleStopRecurrence = async () => {
    if (obligationLocked) return
    const confirmed = await askConfirm({
      title: 'Stop this recurring series?',
      message: 'No new tasks will be created from this series. Tasks that already exist will not be affected.',
      confirmLabel: 'Stop series'
    })
    if (!confirmed) return
    setStoppingRecurrence(true)
    try {
      await stopRecurrence(task.id)
      dispatch(getTaskRoutine(task.id))
    } catch (err) {
      console.error('Failed to stop recurrence:', err)
    } finally {
      setStoppingRecurrence(false)
    }
  }

  const startEditRecurrence = () => {
    if (!canEditSchedule || task.parentTaskId) return
    setEditRecType(task.recurrenceType === 'repeating' ? 'repeating' : 'one_off')
    setEditRecInterval(task.recurrenceInterval || 1)
    setEditRecUnit(task.recurrenceUnit || 'month')
    setEditRecStart(
      task.recurrenceStartDate
        ? moment(task.recurrenceStartDate).format('YYYY-MM-DD')
        : (task.deadline ? moment(task.deadline).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'))
    )
    setEditRecEnd(task.recurrenceEndDate ? moment(task.recurrenceEndDate).format('YYYY-MM-DD') : '')
    setIsEditingRecurrence(true)
  }

  const saveRecurrence = () => {
    const payload = { id: task.id, recurrenceType: editRecType }
    if (editRecType === 'repeating') {
      payload.recurrenceInterval = Number(editRecInterval) || 1
      payload.recurrenceUnit = editRecUnit
      payload.recurrenceStartDate = editRecStart || moment().format('YYYY-MM-DD')
      payload.recurrenceEndDate = editRecEnd || null
    }
    dispatch(updateTaskRoutine(payload))
    setIsEditingRecurrence(false)
  }

  const handlePageScroll = useCallback(event => {
    if (window.innerWidth > 640) return

    const scrollTop = event.currentTarget.scrollTop
    const delta = scrollTop - lastScrollTopRef.current

    if (scrollTop <= 8) {
      setMobileActionBarVisible(true)
    } else if (delta > 6) {
      setMobileActionBarVisible(false)
    } else if (delta < -6) {
      setMobileActionBarVisible(true)
    }

    lastScrollTopRef.current = Math.max(scrollTop, 0)
  }, [])

  if (currentTaskNotFound && (!task || task.id !== id)) {
    return (
      <PageWrapper>
        <Header>
          <HeaderContent>
            <TitleRow>
              <Tooltip title={backLabel}>
                <BackButton type='button' aria-label={backLabel} onClick={handleBack}>
                  <ArrowBackIcon aria-hidden='true' />
                </BackButton>
              </Tooltip>
              <PageTitle>
                <AssignmentOutlinedIcon />
                Task not available
              </PageTitle>
            </TitleRow>
          </HeaderContent>
        </Header>
        <ContentArea>
          <Card>
            <TaskGoneTitle>This task is no longer available</TaskGoneTitle>
            <TaskGoneText>
              It may have been deleted, or you may no longer have access to it.
            </TaskGoneText>
            <TaskGoneButton type='button' onClick={handleBack}>Back to tasks</TaskGoneButton>
          </Card>
        </ContentArea>
      </PageWrapper>
    )
  }

  if (!task || task.id !== id) {
    return (
      <PageWrapper>
        <Header>
          {trackerReturn && (
            <TrackerReturnBar type='button' onClick={() => reactRouterNavigate(trackerReturn)}>
              ← Back to compliance tracker
            </TrackerReturnBar>
          )}
          <HeaderContent>
            <TitleRow>
              <Tooltip title={backLabel}>
                <BackButton type='button' aria-label={backLabel} onClick={handleBack}>
                  <ArrowBackIcon aria-hidden='true' />
                </BackButton>
              </Tooltip>
              <PageTitle>
                <AssignmentOutlinedIcon />
                <SkeletonLine $w='240px' $h={20} />
              </PageTitle>
            </TitleRow>
          </HeaderContent>
        </Header>
        <ContentArea aria-busy='true' aria-label='Loading task'>
          <Card>
            <SkeletonLine $w='55%' $h={22} style={{ marginBottom: 18 }} />
            <SkeletonLine $w='100%' $h={13} style={{ marginBottom: 10 }} />
            <SkeletonLine $w='92%' $h={13} style={{ marginBottom: 10 }} />
            <SkeletonLine $w='38%' $h={13} />
          </Card>
          <Card>
            <PeopleSection>
              {['Assignees', 'External users', 'Approvers'].map(label => (
                <PeopleRow key={label}>
                  <PeopleLabel>{label}</PeopleLabel>
                  <ChipList>
                    {[0, 1].map(i => (
                      <PersonChipSkeleton key={i}>
                        <SkeletonLine $w='26px' $h={26} style={{ borderRadius: '50%', flex: '0 0 auto' }} />
                        <SkeletonLine $w='96px' $h={11} />
                      </PersonChipSkeleton>
                    ))}
                  </ChipList>
                </PeopleRow>
              ))}
            </PeopleSection>
          </Card>
        </ContentArea>
      </PageWrapper>
    )
  }

  const isAssignedToMe =
    currentUser?.id && task.assignees?.some(a => a.id === currentUser.id)
  const isApprover =
    currentUser?.id && task.approvers?.some(a => a.id === currentUser.id)
  const approverAssigneeOverlap = (task.approvers || []).filter(
    ap => (task.assignees || []).some(as => as.id === ap.id)
  )
  const isOnHold = task.status === 'on_hold'
  const displayStatus =
    task.isOverdue && task.status !== 'completed' && task.status !== 'on_hold'
      ? 'overdue'
      : task.status
  const statusMeta = taskStatusMeta(displayStatus)
  const statusInfo = { ...statusMeta, accent: statusMeta.color, Icon: STATUS_ICON[displayStatus] || RadioButtonUncheckedIcon }
  const taskKind = (task.complianceObligation || task.metadata?.programmeObligationId || task.metadata?.programme_obligation_id)
    ? 'obligation'
    : (task.recurrenceType === 'repeating' || task.parentTaskId ? 'recurring' : 'one_off')
  const priorityInfo = task.priority ? priorityConfig[task.priority] : null
  const permissions = task.permissions || {}
  const canHold = permissions.canHold
  const canResume = permissions.canResume
  const canSignOff = permissions.canSignOff
  const isSignedOff = !!task.signedOffAt
  const isEditable =
    ['assigned', 'in_progress'].includes(task.status) && permissions.canSaveForm
  const isReviewable = task.status === 'awaiting_approval'
  const canApprove = isReviewable && permissions.canApprove
  const canEditAsApprover = isReviewable && permissions.canEditAsApprover
  const canManage = permissions.canAssign
  const obligationLocked = !!(
    task.complianceObligation ||
    task.metadata?.programmeObligationId ||
    task.metadata?.programme_obligation_id
  )
  const canEditSchedule = canManage && !obligationLocked

  const scheduleLockHint = 'Set by the linked compliance obligation. Change it on the compliance tracker.'
  const isCompleted = task.status === 'completed'
  const canEditPeople = canManage && !isCompleted
  const StatusIcon = statusInfo.Icon

  const schema = task.workingSchema || task.formTemplate?.schema
  const responses = task.formResponse?.responses || {}
  const schemaSnapshot = task.formResponse?.schemaSnapshot
  const isFileUploadTask = ['analytics_checklist_item', 'task_file_upload'].includes(
    task.metadata?.source
  )
  const canSubmitStatus = ['assigned', 'in_progress'].includes(task.status) && !!permissions.canSubmit
  const submitMode = !canSubmitStatus
    ? null
    : (isEditable && schema)
      ? 'form'
      : (!task.formTemplate && !isCompleted && task.status !== 'cancelled')
        ? 'noForm'
        : null
  const submitLabel = task.requiresApproval ? 'Submit for approval' : 'Submit'
  const handleHeaderSubmit = () => {
    if (submitMode === 'form') formRendererRef.current?.requestSubmit()
    else if (submitMode === 'noForm') assignFormPanelRef.current?.requestSubmit()
  }
  const handleStartForm = () => {
    formRendererRef.current?.scrollToFirstQuestion()
  }
  const nextStep = getNextStep({
    task,
    submitMode,
    canApprove,
    canManage,
    progress: submitMode === 'form' ? formProgress : null,
    blockedMessage: submitMode === 'form' ? submitBlockedMessage : null
  })

  const responseSectionTitle = isFileUploadTask
    ? (isCompleted
      ? 'Completed file upload'
      : isReviewable
        ? 'Review the uploaded file'
        : isEditable
          ? 'Upload the required file'
          : 'File upload (view only)')
    : (isCompleted
      ? 'Completed form'
      : isReviewable
        ? (approverEditing ? 'Editing as approver' : 'Review form response')
        : isEditable
          ? 'Fill in the form'
          : ['assigned', 'in_progress'].includes(task.status)
            ? 'Form response (view only)'
            : 'Form')

  const headerChips = (
    <>
          <StatusChip $color={statusInfo.color} $bg={statusInfo.bg}>
            <StatusIcon style={{ fontSize: 16 }} />
            <span>{statusInfo.label}</span>
          </StatusChip>
          <TaskKindBadge
            $kind={taskKind}
            title={taskKind === 'obligation'
              ? 'A recurring compliance obligation. The programme creates the next task automatically on its schedule.'
              : taskKind === 'recurring'
                ? 'A repeating task. The next one is created automatically on its schedule.'
                : 'A one-off task that happens once and does not repeat.'}
          >
            {taskKind === 'obligation' ? (
              <><VerifiedUserOutlinedIcon style={{ fontSize: 14 }} /> Compliance obligation</>
            ) : taskKind === 'recurring' ? (
              <><RepeatIcon style={{ fontSize: 14 }} /> Recurring{recurrenceLabel(task) ? ` · ${recurrenceLabel(task)}` : ''}</>
            ) : (
              <><RadioButtonUncheckedIcon style={{ fontSize: 13 }} /> One-off task</>
            )}
          </TaskKindBadge>
          {(isEditable || canEditAsApprover) && (
            <SaveStatus
              $state={(formResponseSaving || approverFormSaving) ? 'saving' : lastSavedAt ? 'saved' : 'idle'}
              aria-live='polite'
              title='Your answers save automatically as you go'
            >
              {(formResponseSaving || approverFormSaving) ? (
                <><SaveSpinner /> Saving…</>
              ) : lastSavedAt ? (
                <><CheckCircleOutlineIcon style={{ fontSize: 15 }} /> Saved · {new Date(lastSavedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</>
              ) : (
                <>Changes save automatically</>
              )}
            </SaveStatus>
          )}
    </>
  )
  const primaryHeaderAction = (submitMode || canApprove) ? (
    <>
          {submitMode && (
            <HeaderSubmitBtn
              type='button'
              onClick={handleHeaderSubmit}
              title={task.requiresApproval ? 'Send your answers to the approver' : 'Mark this task as done'}
              data-tour-id='taskDetail.submitTop'
            >
              <TaskAltIcon style={{ fontSize: 18 }} aria-hidden='true' />
              <span>{submitLabel}</span>
            </HeaderSubmitBtn>
          )}
          {canApprove && (
            <HeaderApproveBtn
              type='button'
              onClick={handleApprove}
              title='Approve this task'
              data-tour-id='taskDetail.approve'
            >
              <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
              <span>Approve</span>
            </HeaderApproveBtn>
          )}
    </>
  ) : null
  const secondaryHeaderActions = (
    <>
          {task.building && (
            <OpenBpButton
              onClick={handleOpenBuildingPassport}
              title='Open this Building Passport in another tab'
              aria-label='Open Building Passport in another tab'
            >
              <OpenInNewIcon style={{ fontSize: 16 }} />
              <span>Open Building Passport</span>
            </OpenBpButton>
          )}
          <OpenBpButton
            onClick={() => setQrModalOpen(true)}
            title='Print or scan a QR code that opens this task'
            aria-label='Show QR code for this task'
          >
            <QrCodeIcon style={{ fontSize: 16 }} />
            <span>QR code</span>
          </OpenBpButton>
          {(canManage || permissions.canSaveForm) && task.building?.id && (
            <OpenBpButton
              onClick={() => setFollowUpModalOpen(true)}
              title='Create a follow-up task linked to this one'
              aria-label='Create a follow-up task'
            >
              <AddTaskOutlinedIcon style={{ fontSize: 16 }} />
              <span>Follow-up task</span>
            </OpenBpButton>
          )}
    </>
  )

  return (
    <PageWrapper ref={pageWrapperRef} onScroll={handlePageScroll}>
      <Header data-testid='task-header'>
        {trackerReturn && (
          <TrackerReturnBar type='button' onClick={() => reactRouterNavigate(trackerReturn)}>
            ← Back to compliance tracker
          </TrackerReturnBar>
        )}
        <HeaderContent>
          <TitleRow>
            <Tooltip title={backLabel}>
              <BackButton type='button' aria-label={backLabel} onClick={handleBack}>
                <ArrowBackIcon aria-hidden='true' />
              </BackButton>
            </Tooltip>
            <PageTitle>
            <AssignmentOutlinedIcon />
            {isEditingTitle ? (
              <TitleEditInput
                ref={titleInputRef}
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveTitle()
                  if (e.key === 'Escape') handleCancelEditTitle()
                }}
                onBlur={handleSaveTitle}
              />
            ) : (
              <TitleText
                onClick={canEditPeople ? handleStartEditTitle : undefined}
                $editable={canEditPeople}
              >
                {task.title}
                {canEditPeople && <EditOutlinedIcon style={{ fontSize: 16 }} />}
              </TitleText>
            )}
            </PageTitle>
          </TitleRow>
          {isNarrowHeader ? (
            primaryHeaderAction && <NarrowPrimaryRow>{primaryHeaderAction}</NarrowPrimaryRow>
          ) : (
            <ActionsRow>
              {headerChips}
              {primaryHeaderAction}
              {secondaryHeaderActions}
            </ActionsRow>
          )}
        </HeaderContent>
      </Header>

      {isNarrowHeader && (
        <NarrowMetaRow data-testid='task-header-more'>
          {headerChips}
          {secondaryHeaderActions}
        </NarrowMetaRow>
      )}

      {followUpModalOpen && (
        <FollowUpTaskModal
          task={task}
          onClose={() => setFollowUpModalOpen(false)}
          onCreated={() => dispatch(getTaskRoutine(task.id))}
        />
      )}


      <TaskNextStep
        step={nextStep}
        onSubmit={handleHeaderSubmit}
        onStart={handleStartForm}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      {task.metadata?.rejectionReason && task.status === 'in_progress' && (
        <RejectionBanner>
          <RejectionBannerIcon>
            <CancelOutlinedIcon />
          </RejectionBannerIcon>
          <RejectionBannerContent>
            <RejectionBannerTitle>Task was rejected</RejectionBannerTitle>
            <RejectionBannerReason>
              {task.metadata.rejectionReason}
            </RejectionBannerReason>
          </RejectionBannerContent>
        </RejectionBanner>
      )}

      {isOnHold && (
        <HoldBanner>
          <HoldBannerIcon>
            <PauseCircleOutlineIcon />
          </HoldBannerIcon>
          <HoldBannerContent>
            <HoldBannerTitle>Task is on hold</HoldBannerTitle>
            {task.holdReason && (
              <HoldBannerReason>{task.holdReason}</HoldBannerReason>
            )}
            {task.holdNextAction && (
              <HoldBannerNextAction>
                Next action: {task.holdNextAction}
              </HoldBannerNextAction>
            )}
          </HoldBannerContent>
          {canResume && (
            <ResumeBtn onClick={handleResume}>
              <PlayCircleOutlineIcon />
              <span>Resume</span>
            </ResumeBtn>
          )}
        </HoldBanner>
      )}

      <ContentArea
        $hasActionBar={
          isReviewable ||
          isCompleted ||
          isOnHold ||
          (isEditable && permissions.canSubmit)
        }
      >
        {/* Task Info Card */}
        <Card>
          <TaskOrigin
            taskId={task.id}
            bpId={task.building?.id || task.buildingPassportId}
            bpName={task.building?.name}
          />
          <ImportedTaskDetails task={task} />
          {(task.complianceObligation || canManage) && (
            <ObligationEditWrap>
              {isEditingObligation ? (
                obligationOptions === null ? (
                  <SearchableSelect
                    disabled
                    value={null}
                    options={[]}
                    placeholder='Loading obligations…'
                    ariaLabel='Linked compliance obligation'
                  />
                ) : (
                  <SearchableSelect
                    value={task.complianceObligation?.id || ''}
                    onChange={v => handleSaveObligation(v)}
                    options={[
                      { value: '', label: 'None (not linked to an obligation)' },
                      ...obligationSelectOptions.map(o => ({ value: o.id, label: `${o.name}${o.domainName ? `: ${o.domainName}` : ''}` })),
                    ]}
                    searchPlaceholder='Search obligations…'
                    ariaLabel='Linked compliance obligation'
                  />
                )
              ) : task.complianceObligation ? (
                <>
                  <ComplianceObligationBanner
                    type='button'
                    style={{ margin: 0 }}
                    onClick={() => reactRouterNavigate(`/bp-list/${task.complianceObligation.bpId}/programme?obligation=${task.complianceObligation.id}`)}
                    title='Open the compliance tracker'
                  >
                    <CobIcon><VerifiedUserOutlinedIcon style={{ fontSize: 18 }} /></CobIcon>
                    <CobText>
                      <CobTitle>Satisfies obligation: <strong>{task.complianceObligation.name}</strong></CobTitle>
                      <CobSub>
                        {task.complianceObligation.cadenceDays ? cadenceLabel(task.complianceObligation.cadenceDays) : 'Recurring'}
                        {task.complianceObligation.cadenceWeekday
                          ? ` on ${['', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'][task.complianceObligation.cadenceWeekday]}`
                          : ''}
                        {task.completedAt ? ' · covers this cycle' : ' · completing this satisfies the current cycle'}
                        {task.complianceObligation.nextOccurrenceAt
                          ? ` · next occurs ${moment(task.complianceObligation.nextOccurrenceAt).format('DD MMM YYYY')}`
                          : ''}
                      </CobSub>
                    </CobText>
                    <CobChevron>›</CobChevron>
                  </ComplianceObligationBanner>
                  <ObligationLockNote>
                    <LockOutlinedIcon style={{ fontSize: 13 }} />
                    <span>This task follows the obligation’s schedule. The compliance programme creates the next one automatically, so its repeat and domain can’t be changed here. Adjust the cadence, weekday, criticality and more below.</span>
                  </ObligationLockNote>
                  {canManage && (
                    <ObligationActions>
                      <CustomiseObligationBtn
                        type='button'
                        onClick={handleCustomiseObligation}
                        disabled={customiseLoading}
                        title='Change the schedule, criticality and settings for every task on this obligation'
                      >
                        <TuneRoundedIcon style={{ fontSize: 16 }} />
                        {customiseLoading ? 'Opening…' : 'Customise all tasks for this obligation'}
                      </CustomiseObligationBtn>
                      <AddFieldBtn onClick={handleStartEditObligation}>
                        Change linked obligation
                      </AddFieldBtn>
                    </ObligationActions>
                  )}
                </>
              ) : (
                <AddFieldBtn onClick={handleStartEditObligation}>
                  Link to a compliance obligation
                </AddFieldBtn>
              )}
            </ObligationEditWrap>
          )}

          {(taskKind !== 'one_off') && (occurrencesLoading || occurrences.length > 1 || (taskKind === 'obligation' && occurrences.length >= 1)) && (
            <OccurrencesPanel>
              <OccurrencesHead>
                <RepeatIcon style={{ fontSize: 15 }} />
                <span>{taskKind === 'obligation' ? 'Tasks for this obligation' : 'Tasks in this series'}</span>
                {!occurrencesLoading && <OccurrencesCount>{occurrences.length}</OccurrencesCount>}
                {occurrenceSchedule && <OccScheduleTag>{occurrenceSchedule}</OccScheduleTag>}
              </OccurrencesHead>
              {occurrencesLoading ? (
                <div aria-busy='true' aria-label='Loading the tasks in this series' data-testid='occurrences-skeleton'>
                  <SkeletonRows rows={3} gap={10} widths={['55%', '25%']} />
                </div>
              ) : (
                <OccurrencesList>
                  {occurrences.map(o => {
                    const done = o.status === 'completed'
                    return (
                      <OccurrenceRow
                        key={o.id}
                        $current={o.isCurrent}
                        onClick={() => !o.isCurrent && redirect(PRIVATE_PATHS.taskDetails.replace(':id', o.id))}
                        title={o.isCurrent ? 'This task' : 'Open this task'}
                      >
                        <OccDot $done={done} $overdue={o.isOverdue} />
                        <OccWhen>
                          {done
                            ? `Completed ${o.completedAt ? moment(o.completedAt).format('DD MMM YYYY') : ''}`.trim()
                            : o.deadline ? `Due ${moment(o.deadline).format('DD MMM YYYY')}` : 'No deadline'}
                        </OccWhen>
                        <OccStatus $overdue={o.isOverdue} $done={done}>
                          {o.isOverdue ? 'Overdue' : done ? 'Done' : (o.status || '').replace(/_/g, ' ')}
                        </OccStatus>
                        {o.assigneeAvatars?.length > 0 && (
                          <AssigneeAvatars assignees={o.assigneeAvatars} size={20} />
                        )}
                        {o.isCurrent && <OccCurrentTag>This one</OccCurrentTag>}
                      </OccurrenceRow>
                    )
                  })}
                </OccurrencesList>
              )}
            </OccurrencesPanel>
          )}

          {/* A1: Inline-editable description */}
          {isEditingDescription ? (
            <DescriptionEditWrapper>
              <DescriptionTextarea
                ref={descriptionRef}
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') setIsEditingDescription(false)
                }}
                placeholder='Add a description…'
                rows={3}
              />
              <DescriptionEditActions>
                <InlineSaveBtn onClick={handleSaveDescription}>
                  Save
                </InlineSaveBtn>
                <InlineCancelBtn onClick={() => setIsEditingDescription(false)}>
                  Cancel
                </InlineCancelBtn>
              </DescriptionEditActions>
            </DescriptionEditWrapper>
          ) : task.description ? (
            <TaskDescription
              onClick={canManage ? handleStartEditDescription : undefined}
              $editable={canManage}
            >
              {task.description}
              {canManage && (
                <EditOutlinedIcon
                  style={{
                    fontSize: 14,
                    marginLeft: 6,
                    opacity: 0.4,
                    verticalAlign: 'middle'
                  }}
                />
              )}
            </TaskDescription>
          ) : canManage ? (
            <AddDescriptionBtn onClick={handleStartEditDescription}>
              + Add description
            </AddDescriptionBtn>
          ) : null}


          <MetaGrid>
            {task.building && (
              <MetaRow>
                <MetaIcon>
                  <ApartmentOutlinedIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Building Passport</MetaLabel>
                  {task.building?.id ? (
                    <MetaLink
                      role='link'
                      tabIndex={0}
                      title='Open this Building Passport'
                      onClick={handleOpenBuildingPassport}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleOpenBuildingPassport()
                        }
                      }}
                    >
                      {task.building.name}
                    </MetaLink>
                  ) : (
                    <MetaValue>{task.building.name}</MetaValue>
                  )}
                </MetaContent>
              </MetaRow>
            )}

            {(() => {
              const unitList =
                task.units && task.units.length > 0
                  ? task.units
                  : task.unit
                    ? [task.unit]
                    : []
              if (unitList.length === 0) return null
              return (
                <MetaRow>
                  <MetaIcon>
                    <MeetingRoomOutlinedIcon />
                  </MetaIcon>
                  <MetaContent>
                    <MetaLabel>Unit{unitList.length > 1 ? 's' : ''}</MetaLabel>
                    <MetaValue>
                      {unitList.map(u => u.name).join(', ')}
                    </MetaValue>
                  </MetaContent>
                </MetaRow>
              )
            })()}

            <MetaRow>
              <MetaIcon>
                <GroupsOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Team</MetaLabel>
                {task.team ? (
                  <MetaValue>
                    {task.team.name}
                    {canEditPeople && (
                      <TeamChangeBtn onClick={handleOpenTeamModal}>
                        Change
                      </TeamChangeBtn>
                    )}
                  </MetaValue>
                ) : (
                  <MetaValue>
                    {canEditPeople ? (
                      <TeamChangeBtn onClick={handleOpenTeamModal}>
                        <PersonAddAltOutlinedIcon style={{ fontSize: 14 }} />
                        Assign to team
                      </TeamChangeBtn>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>No team</span>
                    )}
                  </MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            {task.formTemplate && (
              <MetaRow>
                <MetaIcon>
                  <DescriptionOutlinedIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Form template</MetaLabel>
                  <MetaLink
                    onClick={() =>
                      redirect(
                        PRIVATE_PATHS.formBuilderEdit.replace(
                          ':id',
                          task.formTemplate.id
                        )
                      )
                    }
                  >
                    {task.formTemplate.name}
                  </MetaLink>
                  {canManage && (
                    <TaskFormQuickEdit task={task} onSaved={handleRefreshTask}>
                      <ChangeFormLink as='span'>Change form</ChangeFormLink>
                    </TaskFormQuickEdit>
                  )}
                </MetaContent>
              </MetaRow>
            )}

            <MetaRow>
              <MetaIcon>
                <FlagOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Priority</MetaLabel>
                {isEditingPriority ? (
                  <SearchableSelect
                    value={editPriority}
                    onChange={v => handleSavePriority(v)}
                    options={[
                      { value: '', label: 'None' },
                      { value: 'low', label: 'Low', color: PRIORITY_RAG.low },
                      { value: 'medium', label: 'Medium', color: PRIORITY_RAG.medium },
                      { value: 'high', label: 'High', color: PRIORITY_RAG.high },
                      { value: 'critical', label: PRIORITY_META.critical.label, color: PRIORITY_RAG.critical },
                    ]}
                    searchable={false}
                    ariaLabel='Priority'
                  />
                ) : priorityInfo ? (
                  <ClickableMetaValue
                    onClick={canManage ? handleStartEditPriority : undefined}
                    $editable={canManage}
                  >
                    <PriorityChip
                      $color={priorityInfo.color}
                      $bg={priorityInfo.bg}
                    >
                      {priorityInfo.label}
                    </PriorityChip>
                    {canManage && (
                      <EditOutlinedIcon
                        style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }}
                      />
                    )}
                  </ClickableMetaValue>
                ) : canManage ? (
                  <AddFieldBtn onClick={handleStartEditPriority}>
                    Set priority
                  </AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>Not set</MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            <MetaRow>
              <MetaIcon $danger={task.isOverdue && !!task.deadline}>
                <CalendarTodayOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Deadline</MetaLabel>
                {isEditingDeadline ? (
                  <InlineDateInput
                    ref={deadlineRef}
                    type='date'
                    value={editDeadline}
                    onChange={e => setEditDeadline(e.target.value)}
                    onBlur={handleSaveDeadline}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveDeadline()
                      if (e.key === 'Escape') setIsEditingDeadline(false)
                    }}
                  />
                ) : task.deadline ? (
                  <ClickableMetaValue
                    onClick={canEditSchedule ? handleStartEditDeadline : undefined}
                    $editable={canEditSchedule}
                    $danger={task.isOverdue}
                    title={obligationLocked ? scheduleLockHint : undefined}
                  >
                    {moment(task.deadline).format('ddd, DD MMM YYYY')}
                    {canEditSchedule ? (
                      <EditOutlinedIcon
                        style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }}
                      />
                    ) : obligationLocked ? (
                      <LockOutlinedIcon style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }} />
                    ) : null}
                  </ClickableMetaValue>
                ) : canEditSchedule ? (
                  <AddFieldBtn onClick={handleStartEditDeadline}>
                    Set deadline
                  </AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>
                    No deadline
                  </MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            <MetaRow>
              <MetaIcon>
                <FlagOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Task type</MetaLabel>
                {isEditingTaskType ? (
                  <SearchableSelect
                    value={task.taskType || ''}
                    onChange={v => handleSaveTaskType(v)}
                    options={[
                      { value: '', label: 'Unspecified' },
                      { value: 'instructional', label: 'Instructional' },
                      { value: 'data_collection', label: 'Data collection' },
                      { value: 'conditional', label: 'Conditional' },
                    ]}
                    searchable={false}
                    ariaLabel='Task type'
                  />
                ) : task.taskType ? (
                  <ClickableMetaValue
                    onClick={canManage ? handleStartEditTaskType : undefined}
                    $editable={canManage}
                  >
                    {task.taskType === 'instructional' ? 'Instructional'
                      : task.taskType === 'data_collection' ? 'Data collection'
                      : task.taskType === 'conditional' ? 'Conditional'
                      : task.taskType}
                    {canManage && (
                      <EditOutlinedIcon style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }} />
                    )}
                  </ClickableMetaValue>
                ) : canManage ? (
                  <AddFieldBtn onClick={handleStartEditTaskType}>Set task type</AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>Not set</MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            <MetaRow>
              <MetaIcon>
                <EngineeringOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Contractor</MetaLabel>
                {isEditingContractor ? (
                  <SearchableSelect
                    autoOpen
                    onClose={() => setIsEditingContractor(false)}
                    value={task.contractorId}
                    onChange={v => handleSaveContractor(v || '')}
                    options={availableContractors.map(c => ({
                      value: c.id,
                      label: c.name,
                      sublabel: [c.company, c.assignmentBlocked ? 'Unavailable' : (c.assignmentWarnings?.length ? 'Insurance lapsed' : null)].filter(Boolean).join(' · ') || undefined,
                      disabled: c.assignmentBlocked && c.id !== task.contractorId,
                    }))}
                    clearable
                    clearLabel='No contractor'
                    placeholder='No contractor'
                    searchPlaceholder='Search contractors…'
                    ariaLabel='Contractor'
                  />
                ) : contractorName ? (
                  <ClickableMetaValue
                    onClick={canManage ? handleStartEditContractor : undefined}
                    $editable={canManage}
                  >
                    {contractorName}
                    {canManage && (
                      <EditOutlinedIcon style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }} />
                    )}
                  </ClickableMetaValue>
                ) : canManage ? (
                  <AddFieldBtn onClick={handleStartEditContractor}>Assign contractor</AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>No contractor</MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            <MetaRow>
              <MetaIcon>
                <ReceiptLongOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>PO number</MetaLabel>
                {isEditingPoNumber ? (
                  <InlineDateInput
                    type='text'
                    value={editPoNumber}
                    onChange={e => setEditPoNumber(e.target.value)}
                    onBlur={handleSavePoNumber}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSavePoNumber()
                      if (e.key === 'Escape') setIsEditingPoNumber(false)
                    }}
                    placeholder='e.g. PO-2026-001234'
                    autoFocus
                  />
                ) : task.poNumber ? (
                  <ClickableMetaValue
                    onClick={canManage ? handleStartEditPoNumber : undefined}
                    $editable={canManage}
                  >
                    {task.poNumber}
                    {canManage && (
                      <EditOutlinedIcon style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }} />
                    )}
                  </ClickableMetaValue>
                ) : canManage ? (
                  <AddFieldBtn onClick={handleStartEditPoNumber}>Add PO number</AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>None</MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            <MetaRow>
              <MetaIcon>
                <CategoryOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Domain</MetaLabel>
                {isEditingDomain ? (
                  <SearchableSelect
                    value={task.taskDomainId || ''}
                    onChange={v => handleSaveDomain(v)}
                    options={[
                      { value: '', label: 'No domain' },
                      ...domainOptions.map(d => ({ value: d.id, label: d.name })),
                    ]}
                    searchPlaceholder='Search domains…'
                    ariaLabel='Domain'
                  />
                ) : task.taskDomain ? (
                  <ClickableMetaValue
                    onClick={canEditSchedule ? handleStartEditDomain : undefined}
                    $editable={canEditSchedule}
                    title={obligationLocked ? scheduleLockHint : undefined}
                  >
                    <DomainChip $colour={task.taskDomain.colour}>
                      <DomainSwatch $colour={task.taskDomain.colour} />
                      {task.taskDomain.short || task.taskDomain.name}
                    </DomainChip>
                    {canEditSchedule ? (
                      <EditOutlinedIcon
                        style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }}
                      />
                    ) : obligationLocked ? (
                      <LockOutlinedIcon style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }} />
                    ) : null}
                  </ClickableMetaValue>
                ) : canEditSchedule ? (
                  <AddFieldBtn onClick={handleStartEditDomain}>
                    Assign domain
                  </AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>No domain</MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            <MetaRow>
              <MetaIcon>
                <CalendarTodayOutlinedIcon />
              </MetaIcon>
              <MetaContent>
                <MetaLabel>Estimated duration</MetaLabel>
                {isEditingDuration ? (
                  <InlineDateInput
                    type='number'
                    min='1'
                    max='100000'
                    value={editDuration}
                    onChange={e => setEditDuration(e.target.value)}
                    onBlur={handleSaveDuration}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveDuration()
                      if (e.key === 'Escape') setIsEditingDuration(false)
                    }}
                    autoFocus
                    placeholder='minutes'
                  />
                ) : task.estimatedDurationMinutes != null ? (
                  <ClickableMetaValue
                    onClick={canManage ? handleStartEditDuration : undefined}
                    $editable={canManage}
                  >
                    {task.estimatedDurationMinutes >= 60
                      ? `${Math.floor(task.estimatedDurationMinutes / 60)}h ${task.estimatedDurationMinutes % 60 ? `${task.estimatedDurationMinutes % 60}m` : ''}`.trim()
                      : `${task.estimatedDurationMinutes} min`}
                    {canManage && (
                      <EditOutlinedIcon style={{ fontSize: 12, opacity: 0.4, marginLeft: 4 }} />
                    )}
                  </ClickableMetaValue>
                ) : canManage ? (
                  <AddFieldBtn onClick={handleStartEditDuration}>Set duration</AddFieldBtn>
                ) : (
                  <MetaValue style={{ color: '#94a3b8' }}>Not set</MetaValue>
                )}
              </MetaContent>
            </MetaRow>

            {(task.recurrenceType === 'repeating' || (canEditSchedule && !task.parentTaskId)) && (
              <MetaRow>
                <MetaIcon>
                  <RepeatIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Recurrence</MetaLabel>
                  {isEditingRecurrence ? (
                    <RecEditWrap>
                      <SearchableSelect
                        value={editRecType}
                        onChange={v => setEditRecType(v)}
                        options={[
                          { value: 'one_off', label: 'One-off (does not repeat)' },
                          { value: 'repeating', label: 'Repeats on a schedule' },
                        ]}
                        searchable={false}
                        ariaLabel='Recurrence type'
                      />
                      {editRecType === 'repeating' && (
                        <RecEditRow>
                          <span>Every</span>
                          <RecInput type='number' min='1' value={editRecInterval} onChange={e => setEditRecInterval(e.target.value)} />
                          <div style={{ flex: '0 0 auto', width: 120 }}>
                            <SearchableSelect
                              value={editRecUnit}
                              onChange={v => setEditRecUnit(v)}
                              options={[
                                { value: 'week', label: 'week(s)' },
                                { value: 'month', label: 'month(s)' },
                                { value: 'year', label: 'year(s)' },
                              ]}
                              searchable={false}
                              ariaLabel='Recurrence unit'
                            />
                          </div>
                          {editRecUnit === 'week' && (
                            <>
                              <span>on</span>
                              <div style={{ flex: '0 0 auto', width: 140 }}>
                                <SearchableSelect
                                  value={(editRecStart ? moment(editRecStart) : moment()).day()}
                                  onChange={v => {
                                    const dow = v
                                    const today = moment().startOf('day')
                                    const start = editRecStart && moment(editRecStart).isSameOrAfter(today, 'day') ? moment(editRecStart) : today.clone()
                                    setEditRecStart(start.clone().add((dow - start.day() + 7) % 7, 'days').format('YYYY-MM-DD'))
                                  }}
                                  options={[1, 2, 3, 4, 5, 6, 0].map(d => ({ value: d, label: moment().day(d).format('dddd') }))}
                                  ariaLabel='Day of the week'
                                />
                              </div>
                            </>
                          )}
                          {editRecUnit === 'month' && (
                            <>
                              <span>on the</span>
                              <div style={{ flex: '0 0 auto', width: 110 }}>
                                <SearchableSelect
                                  value={(editRecStart ? moment(editRecStart) : moment()).date()}
                                  onChange={v => {
                                    const dom = v
                                    const today = moment().startOf('day')
                                    const base = editRecStart && moment(editRecStart).isSameOrAfter(today, 'day') ? moment(editRecStart) : today.clone()
                                    let d = base.clone().date(Math.min(dom, base.daysInMonth()))
                                    if (d.isBefore(today, 'day')) { d = base.clone().add(1, 'month'); d = d.date(Math.min(dom, d.daysInMonth())) }
                                    setEditRecStart(d.format('YYYY-MM-DD'))
                                  }}
                                  options={Array.from({ length: 31 }, (_, i) => i + 1).map(d => ({ value: d, label: moment().startOf('year').date(d).format('Do') }))}
                                  ariaLabel='Day of the month'
                                />
                              </div>
                            </>
                          )}
                          <span>starting</span>
                          <RecInput type='date' value={editRecStart} onChange={e => setEditRecStart(e.target.value)} />
                          <span>until</span>
                          <RecInput type='date' value={editRecEnd} onChange={e => setEditRecEnd(e.target.value)} />
                          <RecHint>(optional: leave blank for no end)</RecHint>
                        </RecEditRow>
                      )}
                      <RecActions>
                        <RecLinkBtn $primary onClick={saveRecurrence}>Save</RecLinkBtn>
                        <RecLinkBtn onClick={() => setIsEditingRecurrence(false)}>Cancel</RecLinkBtn>
                      </RecActions>
                    </RecEditWrap>
                  ) : task.recurrenceType === 'repeating' ? (
                    <>
                      <MetaValue>
                        {recurrenceLabel(task)}
                        {task.recurrenceStartDate && (
                          <RecurrenceDetail> from {moment(task.recurrenceStartDate).format('DD MMM YYYY')}</RecurrenceDetail>
                        )}
                        {task.recurrenceEndDate && (
                          <RecurrenceDetail> until {moment(task.recurrenceEndDate).format('DD MMM YYYY')}</RecurrenceDetail>
                        )}
                        {!task.recurrenceEndDate && (
                          <RecurrenceDetail> (no end date)</RecurrenceDetail>
                        )}
                      </MetaValue>
                      {canEditSchedule && !task.parentTaskId && (
                        <RecActions>
                          <RecLinkBtn onClick={startEditRecurrence}>Change</RecLinkBtn>
                          <StopRecurrenceBtn
                            onClick={handleStopRecurrence}
                            disabled={stoppingRecurrence}
                          >
                            <StopCircleOutlinedIcon style={{ fontSize: 14 }} />
                            {stoppingRecurrence ? 'Stopping…' : 'Stop recurrence'}
                          </StopRecurrenceBtn>
                        </RecActions>
                      )}
                      {obligationLocked && (
                        <ObligationLockNote title={scheduleLockHint}>
                          <LockOutlinedIcon style={{ fontSize: 12 }} />
                          <span>Set by the linked obligation</span>
                        </ObligationLockNote>
                      )}
                    </>
                  ) : (
                    <RecLinkBtn onClick={startEditRecurrence}>+ Set up a repeat schedule</RecLinkBtn>
                  )}
                </MetaContent>
              </MetaRow>
            )}

            {canManage && !isCompleted && (
              <MetaRow>
                <MetaIcon>
                  <FactCheckOutlinedIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Approval</MetaLabel>
                  <ApprovalSwitch
                    type='button'
                    role='switch'
                    aria-checked={!!task.requiresApproval}
                    $on={!!task.requiresApproval}
                    onClick={handleToggleApproval}
                  >
                    <SwitchTrack $on={!!task.requiresApproval}><SwitchKnob $on={!!task.requiresApproval} /></SwitchTrack>
                    <ApprovalHint>
                      <ApprovalState $on={!!task.requiresApproval}>{task.requiresApproval ? 'Required.' : 'Not required.'}</ApprovalState>{' '}
                      {task.requiresApproval
                        ? 'When someone finishes this task, it goes to an approver to sign off first.'
                        : 'This task can be completed straight away, with no approver sign-off.'}
                    </ApprovalHint>
                  </ApprovalSwitch>
                </MetaContent>
              </MetaRow>
            )}

            {/* A5: Created by */}
            {(task.creator || task.metadata?.imported) && (
              <MetaRow>
                <MetaIcon>
                  <PersonOutlineIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Created by</MetaLabel>
                  <MetaValue>
                    {task.metadata?.imported && task.metadata.imported.creatorDefault !== false
                      ? (task.metadata.imported.owner ? `${task.metadata.imported.owner}, in the previous system` : 'Brought across from the previous system')
                      : `${task.creator?.firstName || ''} ${task.creator?.lastName || ''}`.trim()}
                  </MetaValue>
                </MetaContent>
              </MetaRow>
            )}

            {/* A3/C3: Parent task link */}
            {task.followUpFor && (
              <MetaRow>
                <MetaIcon>
                  <AddTaskOutlinedIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Follow-up of</MetaLabel>
                  <MetaLink
                    onClick={() =>
                      redirect(
                        PRIVATE_PATHS.taskDetails.replace(':id', task.followUpFor.id)
                      )
                    }
                  >
                    {task.followUpFor.title}
                  </MetaLink>
                </MetaContent>
              </MetaRow>
            )}

            {task.followUps?.length > 0 && (
              <MetaRow>
                <MetaIcon>
                  <AddTaskOutlinedIcon />
                </MetaIcon>
                <MetaContent>
                  <MetaLabel>Follow-up tasks ({task.followUps.length})</MetaLabel>
                  <ChildTaskList>
                    {task.followUps.map(followUp => (
                      <ChildTaskLink
                        key={followUp.id}
                        onClick={() =>
                          redirect(
                            PRIVATE_PATHS.taskDetails.replace(':id', followUp.id)
                          )
                        }
                      >
                        <span>{followUp.title}</span>
                        <ChildTaskStatus $completed={followUp.status === 'completed'}>
                          {followUp.status === 'completed' ? 'Completed' : followUp.status}
                        </ChildTaskStatus>
                      </ChildTaskLink>
                    ))}
                  </ChildTaskList>
                </MetaContent>
              </MetaRow>
            )}
          </MetaGrid>

          {task.progress > 0 && (
            <ProgressSection>
              <ProgressHeader>
                <ProgressLabel>Progress</ProgressLabel>
                <ProgressPercent>{task.progress}%</ProgressPercent>
              </ProgressHeader>
              <ProgressBar>
                <ProgressFill $progress={task.progress} />
              </ProgressBar>
            </ProgressSection>
          )}
        </Card>

        {/* People Card */}
        <Card>
          <CardHeader>
            <CardTitle>
              <GroupOutlinedIcon />
              People
            </CardTitle>
          </CardHeader>

          <PeopleSection>
            {task.team && taskTeamData && (
              <TeamShareBanner>
                <TeamShareIcon>
                  <GroupsOutlinedIcon />
                </TeamShareIcon>
                <TeamShareContent>
                  {teamInternalMembers.length > 0 && (
                    <TeamShareSection>
                      <TeamShareText>
                        <strong>
                          {teamInternalMembers.length} internal member
                          {teamInternalMembers.length !== 1 ? 's' : ''}
                        </strong>{' '}
                        of <strong>{task.team.name}</strong> can see this task.
                      </TeamShareText>
                      <TeamMemberList>
                        {teamInternalMembers.map((m, i) => {
                          const alreadyAssigned = task.assignees?.some(
                            a => a.id === m.user.id
                          )
                          return (
                            <TeamMemberItem key={i}>
                              <TeamMemberName>
                                {m.user.firstName} {m.user.lastName}
                              </TeamMemberName>
                              {canEditPeople && !alreadyAssigned ? (
                                <TeamMemberAddBtnInternal
                                  onClick={() =>
                                    handleAddSingleInternalAssignee(m.user.id)
                                  }
                                >
                                  Add
                                </TeamMemberAddBtnInternal>
                              ) : alreadyAssigned ? (
                                <TeamMemberAssigned>
                                  Assigned
                                </TeamMemberAssigned>
                              ) : null}
                            </TeamMemberItem>
                          )
                        })}
                      </TeamMemberList>
                      {canEditPeople &&
                        teamInternalMembers.some(
                          m => !task.assignees?.some(a => a.id === m.user.id)
                        ) && (
                          <AddTeamMembersBtn
                            type='button'
                            onClick={handleAddInternalAsAssignees}
                          >
                            <PersonAddAltOutlinedIcon
                              style={{ fontSize: 15 }}
                            />
                            Add remaining as assignees
                          </AddTeamMembersBtn>
                        )}
                    </TeamShareSection>
                  )}
                  {teamExternalMembers.length > 0 && (
                    <TeamShareSection $external>
                      <TeamShareText $external>
                        <strong>
                          {teamExternalMembers.length} external member
                          {teamExternalMembers.length !== 1 ? 's' : ''}
                        </strong>{' '}
                       . Invite them one by one:
                      </TeamShareText>
                      <TeamExternalList>
                        {teamExternalMembers.map((m, i) => {
                          const alreadyInvited = task.externalAssignees?.some(
                            e => e.email === m.email
                          )
                          return (
                            <TeamExternalItem key={i}>
                              <TeamExternalEmail>
                                {m.name || maskEmail(m.email)}
                              </TeamExternalEmail>
                              {canEditPeople && !alreadyInvited ? (
                                <TeamExternalInviteBtn
                                  onClick={() =>
                                    handleInviteExternalTeamMember(
                                      m.email,
                                      m.name
                                    )
                                  }
                                >
                                  Invite
                                </TeamExternalInviteBtn>
                              ) : alreadyInvited ? (
                                <TeamExternalInvited>
                                  Invited
                                </TeamExternalInvited>
                              ) : null}
                            </TeamExternalItem>
                          )
                        })}
                      </TeamExternalList>
                    </TeamShareSection>
                  )}
                  {teamInternalMembers.length === 0 &&
                    teamExternalMembers.length === 0 && (
                      <TeamShareText>
                        Shared with <strong>{task.team.name}</strong> (no
                        members yet).
                      </TeamShareText>
                    )}
                </TeamShareContent>
              </TeamShareBanner>
            )}

            <PeopleRow>
              <PeopleLabel>
                Assignees
                <PeopleLabelActions>
                  {canEditPeople && !isAssignedToMe && (
                    <AssignToMeBtn onClick={handleAssignToMe}>
                      <PersonOutlineIcon style={{ fontSize: 14 }} />
                      Assign to me
                    </AssignToMeBtn>
                  )}
                  {canEditPeople && (
                    <ManageBtn onClick={handleOpenAssignModal}>
                      <EditOutlinedIcon style={{ fontSize: 14 }} />
                      Manage
                    </ManageBtn>
                  )}
                  {canEditPeople && (
                    <ManageBtn onClick={handleOpenRemindModal}>
                      <NotificationsNoneOutlinedIcon style={{ fontSize: 14 }} />
                      Remind
                    </ManageBtn>
                  )}
                </PeopleLabelActions>
              </PeopleLabel>
              {task.assignees?.length > 0 ? (
                <ChipList>
                  {task.assignees.map((assignee, i) => (
                    <PersonChip key={i}>
                      <UserAvatar firstName={assignee.firstName} lastName={assignee.lastName} email={assignee.email} src={assignee.avatar} size={24} />
                      <span>
                        {assignee.firstName} {assignee.lastName}
                      </span>
                    </PersonChip>
                  ))}
                </ChipList>
              ) : task.metadata?.imported?.peoplePending?.length > 0 ? (
                <PendingPeople>
                  <span>{task.metadata.imported.peoplePending.join(', ')}</span>
                  <PendingNote>Assigned in the previous system. They are not on this Building Passport yet, so they will be added here once they are given access.</PendingNote>
                </PendingPeople>
              ) : (
                <EmptyText>{task.metadata?.imported && task.status === 'completed' ? 'Not recorded in the previous system' : 'No assignees yet'}</EmptyText>
              )}
            </PeopleRow>

            <PeopleRow>
              <PeopleLabel>
                External users
                {canEditPeople && (
                  <ManageBtn onClick={handleOpenExternalModal}>
                    <PersonAddAltOutlinedIcon style={{ fontSize: 14 }} />
                    Invite
                  </ManageBtn>
                )}
              </PeopleLabel>
              {task.externalAssignees?.length > 0 ? (
                <ChipList>
                  {task.externalAssignees.map((ext, i) => (
                    <PersonChip key={i} $external>
                      <UserAvatar firstName={(ext.name || '').split(' ')[0] || ext.name} lastName={(ext.name || '').split(' ').slice(1).join(' ')} email={ext.email} size={24} />
                      <PersonInfo>
                        <span>{ext.name || maskEmail(ext.email)}</span>
                        {ext.name && (
                          <PersonEmail>{maskEmail(ext.email)}</PersonEmail>
                        )}
                      </PersonInfo>
                      {canEditPeople && (
                        <QrBtn
                          onClick={() => setExtQr(ext)}
                          title='Show a QR code to share this task with the contractor (no login needed)'
                        >
                          <QrCodeIcon style={{ fontSize: 14 }} />
                        </QrBtn>
                      )}
                      {canEditPeople && (
                        <RemoveBtn onClick={() => handleRemoveExternal(ext.id)}>
                          <CloseIcon style={{ fontSize: 14 }} />
                        </RemoveBtn>
                      )}
                    </PersonChip>
                  ))}
                </ChipList>
              ) : (
                <EmptyText>No external users invited</EmptyText>
              )}
            </PeopleRow>

            <PeopleRow>
              <PeopleLabel>
                Approvers
                <PeopleLabelActions>
                  {canEditPeople && !isApprover && (
                    <AssignToMeBtn onClick={handleAddMeAsApprover}>
                      <PersonOutlineIcon style={{ fontSize: 14 }} />
                      Add me as approver
                    </AssignToMeBtn>
                  )}
                  {canEditPeople && (
                    <ManageBtn onClick={handleOpenApproverModal}>
                      <PersonAddAltOutlinedIcon style={{ fontSize: 14 }} />
                      Manage
                    </ManageBtn>
                  )}
                </PeopleLabelActions>
              </PeopleLabel>
              {task.approvers?.length > 0 ? (
                <ChipList>
                  {task.approvers.map((approver, i) => (
                    <PersonChip key={i} $approver>
                      <UserAvatar firstName={approver.firstName} lastName={approver.lastName} email={approver.email} src={approver.avatar} size={24} />
                      <span>
                        {approver.firstName} {approver.lastName}
                      </span>
                    </PersonChip>
                  ))}
                </ChipList>
              ) : (
                <EmptyText>
                  No approvers set. This task will complete directly on submit.
                </EmptyText>
              )}
              {approverAssigneeOverlap.length > 0 && (
                <OverlapWarn>
                  {approverAssigneeOverlap.length === 1
                    ? `${approverAssigneeOverlap[0].firstName} ${approverAssigneeOverlap[0].lastName} is both an assignee and an approver, so they’d be signing off their own work. Consider adding a different approver so the work is checked independently.`
                    : `${approverAssigneeOverlap.map(p => `${p.firstName} ${p.lastName}`).join(', ')} are set as both assignee and approver, so they’d be signing off their own work. Consider adding a different approver so the work is checked independently.`}
                </OverlapWarn>
              )}
            </PeopleRow>
          </PeopleSection>
        </Card>

        {/* Dependencies Card */}
        <Card>
          <CardHeader>
            <CardTitle>
              <LinkIcon />
              Dependencies
            </CardTitle>
            {canManage && (
              <ManageBtn onClick={handleOpenBlockersModal}>
                <EditOutlinedIcon style={{ fontSize: 14 }} />
                Manage
              </ManageBtn>
            )}
          </CardHeader>

          {task.hasIncompleteBlockers && (
            <WarningBanner>
              <ErrorOutlineIcon />
              Some blocking tasks are not yet completed
            </WarningBanner>
          )}

          {task.blockedBy?.length > 0 ? (
            <DependencyList>
              {task.blockedBy.map((blocker, i) => (
                <DependencyItem
                  key={i}
                  $completed={blocker.status === 'completed'}
                  $clickable
                  onClick={() =>
                    redirect(
                      PRIVATE_PATHS.taskDetails.replace(':id', blocker.id)
                    )
                  }
                >
                  <DependencyIcon $completed={blocker.status === 'completed'}>
                    {blocker.status === 'completed' ? (
                      <TaskAltIcon />
                    ) : (
                      <HourglassEmptyIcon />
                    )}
                  </DependencyIcon>
                  <DependencyInfo>
                    <DependencyTitle>{blocker.title}</DependencyTitle>
                    <DependencyStatus
                      $completed={blocker.status === 'completed'}
                    >
                      {blocker.status === 'completed'
                        ? 'Completed'
                        : blocker.status}
                    </DependencyStatus>
                  </DependencyInfo>
                </DependencyItem>
              ))}
            </DependencyList>
          ) : (
            <EmptyText style={{ padding: '16px 0' }}>
              No blocking tasks
            </EmptyText>
          )}

          {/* A4: Blocks (tasks this one blocks) */}
          {task.blocks?.length > 0 && (
            <>
              <DependencySectionLabel>Blocks</DependencySectionLabel>
              <DependencyList>
                {task.blocks.map((blocked, i) => (
                  <DependencyItem
                    key={i}
                    $completed={blocked.status === 'completed'}
                    $clickable
                    onClick={() =>
                      redirect(
                        PRIVATE_PATHS.taskDetails.replace(':id', blocked.id)
                      )
                    }
                  >
                    <DependencyIcon $completed={blocked.status === 'completed'}>
                      {blocked.status === 'completed' ? (
                        <TaskAltIcon />
                      ) : (
                        <HourglassEmptyIcon />
                      )}
                    </DependencyIcon>
                    <DependencyInfo>
                      <DependencyTitle>{blocked.title}</DependencyTitle>
                      <DependencyStatus
                        $completed={blocked.status === 'completed'}
                      >
                        {blocked.status === 'completed'
                          ? 'Completed'
                          : blocked.status}
                      </DependencyStatus>
                    </DependencyInfo>
                  </DependencyItem>
                ))}
              </DependencyList>
            </>
          )}
        </Card>

        {/* Form Section */}
        <Card $mobileFirst={isEditable || isReviewable || approverEditing}>
          <CardHeader>
            <CardTitle>
              <DescriptionOutlinedIcon />
              {responseSectionTitle}
            </CardTitle>
            <CardHeaderActions>
              {canEditAsApprover && (
                <ApproverEditToggle
                  $active={approverEditing}
                  onClick={() => setApproverEditing(!approverEditing)}
                >
                  <EditOutlinedIcon style={{ fontSize: 16 }} />
                  {approverEditing ? 'Stop editing' : 'Edit submission'}
                </ApproverEditToggle>
              )}
              {!isOnline ? (
                <OfflineChip>
                  <WifiOffIcon style={{ fontSize: 14 }} />
                  Offline
                </OfflineChip>
              ) : (
                (isEditable || approverEditing) && (
                  <OnlineChip>
                    <OnlineDot />
                    Online
                  </OnlineChip>
                )
              )}
            </CardHeaderActions>
          </CardHeader>

          {approverEditing && (
            <ApproverEditBanner>
              <EditOutlinedIcon />
              Editing as approver. Original submission is preserved.
            </ApproverEditBanner>
          )}

          {!isOnline && (
            <WarningBanner>
              <WifiOffIcon />
              You're offline - changes will be saved when you reconnect
            </WarningBanner>
          )}

          <SubmissionLocationBanner location={task.formResponse?.submissionLocation} />
          {(isCompleted || task.status === 'awaiting_approval') && <ScoreSummaryCard summary={task.scoreSummary} />}

          <FormArea ref={formAreaRef}>
            {canManage && !isCompleted && task.status !== 'cancelled' && isGenericCompletionForm(task.formTemplate) && (task.metadata?.programmeObligationId || task.metadata?.programme_obligation_id) && (
              <GenericFormTip
                obligationId={task.metadata?.programmeObligationId || task.metadata?.programme_obligation_id}
                buildingPassportId={task.building?.id || task.buildingPassportId}
              />
            )}
            {isEditable && schema ? (
              <FormRenderer
                ref={formRendererRef}
                onProgressChange={setFormProgress}
                onSubmitBlocked={setSubmitBlockedMessage}
                schema={schema}
                responses={responses}
                onSave={handleSaveForm}
                onSubmit={permissions.canSubmit ? handleSubmitForm : null}
                onAutoSave={handleAutoSaveForm}
                lastSavedAt={lastSavedAt}
                isOnline={isOnline}
                saving={formResponseSaving}
                taskId={task.id}
                task={task}
                onTaskRefresh={handleRefreshTask}
                autoPopulateKeys={autoPopulateKeys}
              />
            ) : isReviewable &&
              approverEditing &&
              (schemaSnapshot || schema) ? (
              <FormRenderer
                schema={schemaSnapshot || schema}
                responses={responses}
                onSave={handleApproverSaveForm}
                onAutoSave={null}
                lastSavedAt={null}
                isOnline={isOnline}
                saving={approverFormSaving}
                taskId={task.id}
                task={task}
                onTaskRefresh={handleRefreshTask}
                autoPopulateKeys={autoPopulateKeys}
              />
            ) : ['assigned', 'in_progress'].includes(task.status) &&
              !permissions.canSaveForm &&
              (schemaSnapshot || schema) ? (
              <FormResponseViewer
                schema={schemaSnapshot || schema}
                responses={responses}
                taskId={task.id}
                task={task}
                onTaskRefresh={handleRefreshTask}
              />
            ) : (isReviewable || isCompleted) && (schemaSnapshot || schema) ? (
              <FormResponseViewer
                schema={schemaSnapshot || schema}
                responses={responses}
                taskId={task.id}
                task={task}
                onTaskRefresh={handleRefreshTask}
              />
            ) : task.status === 'unassigned' && (schemaSnapshot || schema) ? (
              <EmptyFormMessage>
                <AssignmentOutlinedIcon />
                <span>
                  This task needs to be assigned before the form can be filled
                  in.
                </span>
              </EmptyFormMessage>
            ) : !task.formTemplate && !isCompleted && task.status !== 'cancelled' ? (
              <AssignFormPanel
                ref={assignFormPanelRef}
                taskId={task.id}
                canManage={canManage}
                buildingPassportId={task.building?.id || task.buildingPassportId}
                programmeObligationId={task.metadata?.programmeObligationId || task.metadata?.programme_obligation_id}
                onAssigned={handleRefreshTask}
                canSubmit={['assigned', 'in_progress'].includes(task.status) && permissions.canSubmit}
                requiresApproval={task.requiresApproval}
              />
            ) : (
              <EmptyFormMessage>
                <DescriptionOutlinedIcon />
                <span>No form data available.</span>
              </EmptyFormMessage>
            )}
          </FormArea>
        </Card>

        <Card>
          <TaskRelatedDocuments
            taskId={task.id}
            bpId={task.building?.id || task.buildingPassportId}
            bpName={task.building?.name}
            canManage={canManage}
          />
        </Card>

        {/* Reference documents (SOPs / guides) */}
        <Card>
          <TaskReferenceFiles
            task={task}
            canManage={canManage}
            onReload={handleRefreshTask}
          />
        </Card>

        {/* Activity Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>
              <HistoryIcon />
              Activity history
              {task.activityLogs?.length > 0 && (
                <ActivityCount>{task.activityLogs.length}</ActivityCount>
              )}
            </CardTitle>
            <VersionHistoryToggle onClick={() => setActivityTimelineOpen(o => !o)}>
              {activityTimelineOpen ? 'Hide' : 'Show'}
            </VersionHistoryToggle>
          </CardHeader>
          {activityTimelineOpen && (
            <TaskActivityTimeline activityLogs={task.activityLogs} />
          )}
        </Card>

        {/* Version History */}
        <Card>
          <CardHeader>
            <CardTitle>
              <HistoryIcon />
              Form version history
            </CardTitle>
            <VersionHistoryToggle onClick={handleToggleVersionHistory}>
              {versionHistoryOpen ? 'Hide' : 'Show'}
            </VersionHistoryToggle>
          </CardHeader>
          {versionHistoryOpen && (
            <FormVersionHistory
              versions={formVersions}
              loading={formVersionsLoading}
            />
          )}
        </Card>

        {/* Conflict Resolution Panel */}
        {isReviewable && canApprove && conflicts.length > 0 && (
          <ConflictResolutionPanel conflicts={conflicts} />
        )}

        {/* Action Bar for Reviewable Tasks */}
        {isReviewable && canApprove && (
          <ActionBar ref={setActionBarEl} $mobileHidden={!mobileActionBarVisible}>
            <RejectBtn onClick={handleReject}>
              <CancelOutlinedIcon />
              <span>Reject</span>
            </RejectBtn>
            <ApproveBtn onClick={handleApprove}>
              <CheckCircleOutlineIcon />
              <span>Approve</span>
            </ApproveBtn>
          </ActionBar>
        )}

        {/* Info banner for reviewable tasks when user is not an approver */}
        {isReviewable && !canApprove && (
          <ActionBar ref={setActionBarEl} $mobileHidden={!mobileActionBarVisible}>
            <ReviewInfoText>
              <PendingActionsIcon style={{ fontSize: 18 }} />
              This task is awaiting approval from a designated approver.
            </ReviewInfoText>
          </ActionBar>
        )}

        {/* Sign-off banner for completed tasks */}
        {isCompleted && isSignedOff && (
          <SignOffBanner $signedOff>
            <SignOffBannerIcon $signedOff>
              <AssignmentTurnedInOutlinedIcon />
            </SignOffBannerIcon>
            <SignOffBannerContent>
              <SignOffBannerTitle>Signed off</SignOffBannerTitle>
              {task.signOff?.signedOffBy && (
                <SignOffBannerDetail>
                  by{' '}
                  <strong>
                    {task.signOff.signedOffBy.firstName}{' '}
                    {task.signOff.signedOffBy.lastName}
                  </strong>
                  {task.signOff.signedOffAt &&
                    ` on ${moment(task.signOff.signedOffAt).format('DD MMM YYYY, HH:mm')}`}
                </SignOffBannerDetail>
              )}
              {task.signOff?.notes && (
                <SignOffBannerNotes>{task.signOff.notes}</SignOffBannerNotes>
              )}
              {task.signOff?.signature && (
                <SignOffBannerSignature>
                  <img src={task.signOff.signature} alt='Sign-off signature' />
                </SignOffBannerSignature>
              )}
            </SignOffBannerContent>
          </SignOffBanner>
        )}

        {/* After sign-off, surface any actions Guru extracted from the
            completed form so the BP user can approve or dismiss them
            before they hit the live Open queue. The panel auto-hides
            when the queue is empty (or never received any candidates). */}
        {isCompleted && isSignedOff && (
          <TaskPendingActionsPanel taskId={task.id} />
        )}

        {isCompleted && !isSignedOff && task.requiresSignOff && (
          <SignOffBanner>
            <SignOffBannerIcon>
              <AssignmentTurnedInOutlinedIcon />
            </SignOffBannerIcon>
            <SignOffBannerContent>
              <SignOffBannerTitle>Awaiting sign-off</SignOffBannerTitle>
              <SignOffBannerDetail>
                This task requires a BP owner to sign off on its completion.
              </SignOffBannerDetail>
            </SignOffBannerContent>
            {canSignOff && (
              <SignOffBtn onClick={handleSignOff}>
                <AssignmentTurnedInOutlinedIcon />
                <span>Sign off</span>
              </SignOffBtn>
            )}
          </SignOffBanner>
        )}

        {/* Action Bar for Completed Tasks */}
        {isCompleted && (
          <ActionBar ref={setActionBarEl} $completed $mobileHidden={!mobileActionBarVisible}>
            {canSignOff && !isSignedOff && !task.requiresSignOff && (
              <SignOffBtn onClick={handleSignOff}>
                <AssignmentTurnedInOutlinedIcon />
                <span>Sign off</span>
              </SignOffBtn>
            )}
            {completedReport && (
              <DownloadReportBtn
                onClick={handleDownloadReport}
                disabled={reportDownloading}
              >
                <FileDownloadOutlinedIcon />
                <span>
                  {reportDownloading ? 'Downloading…' : 'Download report'}
                </span>
              </DownloadReportBtn>
            )}
            <ReplicateBtn onClick={handleReplicateAsDraft}>
              <ContentCopyIcon />
              <span>Replicate as new draft</span>
            </ReplicateBtn>
          </ActionBar>
        )}

        {/* Hold button for editable tasks */}
        {canHold && !isOnHold && !isReviewable && !isCompleted && (
          <ActionBar ref={setActionBarEl} $mobileHidden={!mobileActionBarVisible}>
            <HoldBtn onClick={handleHold}>
              <PauseCircleOutlineIcon />
              <span>Put on hold</span>
            </HoldBtn>
          </ActionBar>
        )}
      </ContentArea>

      {/* Reject Modal */}
      {customiseObl && (
        <CustomiseObligationModal
          bpId={task?.complianceObligation?.bpId || task?.building?.id}
          bpName={customiseCtx.bpName}
          obligation={customiseObl}
          availableTags={customiseCtx.tags}
          availableDomains={customiseCtx.domains}
          onClose={() => setCustomiseObl(null)}
          onSaved={() => { setCustomiseObl(null); if (task?.id) dispatch(getTaskRoutine(task.id)) }}
        />
      )}

      {seriesScopePrompt}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          tone={confirmState.tone}
          onConfirm={() => closeConfirm(true)}
          onClose={() => closeConfirm(false)}
        />
      )}

      {rejectModalOpen && (
        <ModalOverlay onClick={() => setRejectModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Reject task</ModalTitle>
              <ModalClose onClick={() => setRejectModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Please provide a reason for rejecting this task. The assignee
                will be notified.
              </ModalText>
              <ModalTextarea
                placeholder='Say why you are rejecting it…'
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={4}
              />
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setRejectModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalDangerBtn
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
              >
                Reject task
              </ModalDangerBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Hold Modal */}
      {holdModalOpen && (
        <ModalOverlay onClick={() => setHoldModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Put task on hold</ModalTitle>
              <ModalClose onClick={() => setHoldModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Please provide a reason for putting this task on hold.
              </ModalText>
              <ModalTextarea
                placeholder='Say why it is on hold…'
                value={holdReason}
                onChange={e => setHoldReason(e.target.value)}
                rows={3}
              />
              <ModalText style={{ marginTop: 12 }}>
                Next action (optional)
              </ModalText>
              <ModalInput
                placeholder='What needs to happen before resuming?'
                value={holdNextAction}
                onChange={e => setHoldNextAction(e.target.value)}
              />
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setHoldModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalDangerBtn
                onClick={handleHoldConfirm}
                disabled={!holdReason.trim()}
                style={{ background: '#d97706', borderColor: '#d97706' }}
              >
                Put on hold
              </ModalDangerBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Sign-Off Modal */}
      {signOffModalOpen && (
        <ModalOverlay onClick={() => setSignOffModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Sign off task</ModalTitle>
              <ModalClose onClick={() => setSignOffModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                By signing off, you acknowledge that this completed task has
                been reviewed and is satisfactory.
              </ModalText>
              <SignOffSigLabel>Signature</SignOffSigLabel>
              <SignOffSigCanvasWrapper>
                <SignatureCanvas
                  ref={signOffSigRef}
                  canvasProps={{
                    className: 'signoff-signature-canvas',
                    style: { width: '100%', height: '150px' }
                  }}
                  onEnd={() => setSignOffSignature(true)}
                />
              </SignOffSigCanvasWrapper>
              <SignOffSigActions>
                <SignOffSigClearBtn
                  type='button'
                  onClick={handleClearSignOffSignature}
                >
                  Clear
                </SignOffSigClearBtn>
                <SignOffSigHint>Draw your signature above</SignOffSigHint>
              </SignOffSigActions>
              <ModalTextarea
                placeholder='Add notes (optional)…'
                value={signOffNotes}
                onChange={e => setSignOffNotes(e.target.value)}
                rows={3}
              />
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setSignOffModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalDangerBtn
                onClick={handleSignOffConfirm}
                style={{ background: '#059669', borderColor: '#059669' }}
              >
                Confirm Sign-Off
              </ModalDangerBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Assign Modal */}
      {assignModalOpen && (
        <ModalOverlay onClick={() => setAssignModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Manage assignees</ModalTitle>
              <ModalClose onClick={() => setAssignModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody $tall>
              <RolesSummary>
                <RolesSummaryTitle>Currently on this task</RolesSummaryTitle>
                <RoleLine>
                  <RoleTag>Contractor</RoleTag>
                  <RoleValue $muted={!task.contractor}>{task.contractor?.name || 'None'}</RoleValue>
                </RoleLine>
                <RoleLine>
                  <RoleTag>Assignees</RoleTag>
                  <RoleValue $muted={!task.assignees?.length}>
                    {task.assignees?.length
                      ? task.assignees.map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || hideEmail(a.email)).join(', ')
                      : 'None'}
                  </RoleValue>
                </RoleLine>
                <RoleLine>
                  <RoleTag>External</RoleTag>
                  <RoleValue $muted={!task.externalAssignees?.length}>
                    {task.externalAssignees?.length
                      ? task.externalAssignees.map(e => e.name || hideEmail(e.email)).join(', ')
                      : 'None'}
                  </RoleValue>
                </RoleLine>
                <RoleLine>
                  <RoleTag>Approvers</RoleTag>
                  <RoleValue $muted={!task.approvers?.length}>
                    {task.approvers?.length
                      ? task.approvers.map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || hideEmail(a.email)).join(', ')
                      : 'None'}
                  </RoleValue>
                </RoleLine>
              </RolesSummary>

              <ModalText>
                Tick the people who should be assigned. Whoever is ticked when you save becomes this task's assignees; anyone you untick is removed.
              </ModalText>

              <AssignSearchRow>
                <SearchRoundedIcon style={{ fontSize: 18, color: '#9aa0ab' }} />
                <AssignSearchInput
                  placeholder='Search people…'
                  value={assignableSearch}
                  onChange={e => setAssignableSearch(e.target.value)}
                />
              </AssignSearchRow>

              {assignPeopleList.length > 0 && (
                <SelectAllRow type='button' onClick={toggleSelectAllAssignees}>
                  <PeopleTick $on={allAssigneesTicked}>{allAssigneesTicked ? '✓' : ''}</PeopleTick>
                  <span>{allAssigneesTicked ? 'Clear all' : `Select all (${assignPeopleList.length})`}</span>
                </SelectAllRow>
              )}

              <PeopleTickList>
                {assignPeopleList.map(u => {
                  const checked = assigneeIds.includes(u.id)
                  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name || (u.email ? hideEmail(u.email) : 'Unknown')
                  return (
                    <PersonTickRow key={u.id} type='button' $checked={checked} onClick={() => toggleAssignee(u.id)}>
                      <PeopleTick $on={checked}>{checked ? '✓' : ''}</PeopleTick>
                      <PersonTickInfo>
                        <PersonTickName>{name}</PersonTickName>
                        <PersonTickMeta>
                          {u.email ? hideEmail(u.email) : ''}
                          {u.openTaskCount ? ` · ${u.openTaskCount} open task${u.openTaskCount === 1 ? '' : 's'}` : ''}
                        </PersonTickMeta>
                      </PersonTickInfo>
                    </PersonTickRow>
                  )
                })}
                {loadingAssignable && assignPeopleList.length === 0 && <ModalText>Loading people…</ModalText>}
                {!loadingAssignable && assignPeopleList.length === 0 && (
                  <ModalText>No people found{assignableSearch ? ` for “${assignableSearch}”` : ' for this building'}.</ModalText>
                )}
              </PeopleTickList>
              {requiredCerts?.length > 0 && certWarnings.length > 0 && (
                <CertWarningBanner role="status" aria-live="polite">
                  <WarningAmberOutlinedIcon style={{ fontSize: 16, flexShrink: 0 }} />
                  <CertWarningBody>
                    <strong>Heads up. Some assignees are missing required certifications.</strong>
                    <CertWarningList>
                      {certWarnings.map((w, i) => (
                        <li key={i}>
                          <em>{w.userName}</em>
                          {' '}{w.reason === 'expired' ? 'has an expired ' : 'is missing '}
                          <strong>{w.missing}</strong>
                          {w.reason === 'expired' && w.expiredAt && (
                            <> (expired {new Date(w.expiredAt).toLocaleDateString()})</>
                          )}
                          .
                        </li>
                      ))}
                    </CertWarningList>
                    <CertWarningFoot>
                      This is advisory only. Assignment will still go through.
                    </CertWarningFoot>
                  </CertWarningBody>
                </CertWarningBanner>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setAssignModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalAddKeepBtn
                onClick={handleAddAssignees}
                title='Add the ticked people without removing anyone already assigned'
              >
                Add (keep existing)
              </ModalAddKeepBtn>
              <ModalPrimaryBtn onClick={handleSaveAssignees}>
                Save ({assigneeIds.length} assigned)
              </ModalPrimaryBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {remindModalOpen && (
        <ModalOverlay onClick={() => setRemindModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Send a reminder</ModalTitle>
              <ModalClose onClick={() => setRemindModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody $tall>
              <ModalText>
                Choose who to remind. They'll get an in-app notification and an email with a link straight to the form.
              </ModalText>
              <UserPicker
                value={remindIds}
                onChange={setRemindIds}
                buildingPassportId={task.building?.id}
                placeholder='Search people…'
                initialUsers={task.assignees || []}
              />
              <ReminderNote
                value={remindNote}
                onChange={e => setRemindNote(e.target.value)}
                placeholder='Add an optional note (e.g. "Please complete by Friday")'
                maxLength={1000}
                rows={3}
              />
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setRemindModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalPrimaryBtn onClick={handleSendReminder} disabled={remindSending || remindIds.length === 0}>
                {remindSending ? 'Sending…' : 'Send reminder'}
              </ModalPrimaryBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Approver Modal */}
      {approverModalOpen && (
        <ModalOverlay onClick={dismissApproverModal}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Manage approvers</ModalTitle>
              <ModalClose onClick={dismissApproverModal}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody $tall>
              <ModalText>
                Select users who will review and approve submissions for this
                task. The same person can be both an assignee and an approver, though they’d then be approving their own work.
              </ModalText>
              <UserPicker
                value={approverIds}
                onChange={setApproverIds}
                buildingPassportId={task.building?.id}
                placeholder='Search approvers…'
                initialUsers={task.approvers || []}
              />
              {approverIds.some(id => (task.assignees || []).some(a => a.id === id)) && (
                <OverlapWarn>
                  The same person is set as both an assignee and an approver, so they’d be approving their own work. You can still save.
                </OverlapWarn>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={dismissApproverModal}>
                Cancel
              </ModalCancelBtn>
              <ModalPrimaryBtn onClick={handleSaveApprovers}>
                Save approvers
              </ModalPrimaryBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Blockers Modal */}
      {blockersModalOpen && (
        <ModalOverlay onClick={() => setBlockersModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()} $wide>
            <ModalHeader>
              <ModalTitle>Manage dependencies</ModalTitle>
              <ModalClose onClick={() => setBlockersModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Select tasks that must be completed before this task can
                proceed.
              </ModalText>
              <BlockerList>
                {allTasks
                  .filter(t => t.id !== task.id)
                  .map(t => (
                    <BlockerItem
                      key={t.id}
                      onClick={() => toggleBlocker(t.id)}
                      $selected={selectedBlockerIds.includes(t.id)}
                    >
                      <BlockerCheckbox
                        $checked={selectedBlockerIds.includes(t.id)}
                      >
                        {selectedBlockerIds.includes(t.id) && (
                          <CheckCircleOutlineIcon />
                        )}
                      </BlockerCheckbox>
                      <BlockerContent>
                        <BlockerTitle>{t.title}</BlockerTitle>
                        <BlockerMeta>
                          {t.status} • {t.building?.name || 'No Building Passport'}
                        </BlockerMeta>
                      </BlockerContent>
                    </BlockerItem>
                  ))}
                {allTasks.filter(t => t.id !== task.id).length === 0 && (
                  <EmptyText style={{ padding: 24, textAlign: 'center' }}>
                    No other tasks available
                  </EmptyText>
                )}
              </BlockerList>
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setBlockersModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalPrimaryBtn onClick={handleSaveBlockers}>
                Save dependencies
              </ModalPrimaryBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Team Modal */}
      {teamModalOpen && (
        <ModalOverlay onClick={() => setTeamModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Assign to Team</ModalTitle>
              <ModalClose onClick={() => setTeamModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Select a team to share this task with. All team members will be
                able to see and access this task.
              </ModalText>
              <TeamSelectList>
                <TeamSelectItem
                  $selected={!selectedTeamId}
                  onClick={() => setSelectedTeamId('')}
                >
                  <TeamSelectRadio $checked={!selectedTeamId} />
                  <TeamSelectInfo>
                    <TeamSelectName>No team</TeamSelectName>
                    <TeamSelectMeta>
                      Task will be visible to BP users and assignees only
                    </TeamSelectMeta>
                  </TeamSelectInfo>
                </TeamSelectItem>
                {teams
                  .filter(t => !t.isSystem)
                  .map(team => (
                    <TeamSelectItem
                      key={team.id}
                      $selected={selectedTeamId === team.id}
                      onClick={() => setSelectedTeamId(team.id)}
                    >
                      <TeamSelectRadio $checked={selectedTeamId === team.id}>
                        {selectedTeamId === team.id && (
                          <CheckCircleOutlineIcon />
                        )}
                      </TeamSelectRadio>
                      <TeamSelectInfo>
                        <TeamSelectName>{team.name}</TeamSelectName>
                        <TeamSelectMeta>
                          {team.members?.filter(m => !m.isPending).length ||
                            team.memberCount ||
                            0}{' '}
                          members
                        </TeamSelectMeta>
                        {selectedTeamId === team.id &&
                          selectedTeamData?.members?.length > 0 && (
                            <TeamMemberPreview>
                              {selectedTeamData.members
                                .filter(m => m.user && !m.isPending)
                                .slice(0, 4)
                                .map((m, i) => (
                                  <TeamMemberChip key={i}>
                                    {m.user.firstName} {m.user.lastName}
                                  </TeamMemberChip>
                                ))}
                              {selectedTeamData.members.filter(
                                m => m.user && !m.isPending
                              ).length > 4 && (
                                <TeamMemberMore>
                                  +
                                  {selectedTeamData.members.filter(
                                    m => m.user && !m.isPending
                                  ).length - 4}{' '}
                                  more
                                </TeamMemberMore>
                              )}
                            </TeamMemberPreview>
                          )}
                      </TeamSelectInfo>
                    </TeamSelectItem>
                  ))}
                {teams.some(t => t.isSystem) && (
                  <>
                    <TeamSectionDivider>
                      <TeamSectionDividerLine />
                      <TeamSectionDividerText>
                        Your organisation
                      </TeamSectionDividerText>
                      <TeamSectionDividerLine />
                    </TeamSectionDivider>
                    {teams
                      .filter(t => t.isSystem)
                      .map(team => (
                        <TeamSelectItem
                          key={team.id}
                          $selected={selectedTeamId === team.id}
                          onClick={() => setSelectedTeamId(team.id)}
                        >
                          <TeamSelectRadio
                            $checked={selectedTeamId === team.id}
                          >
                            {selectedTeamId === team.id && (
                              <CheckCircleOutlineIcon />
                            )}
                          </TeamSelectRadio>
                          <TeamSelectInfo>
                            <TeamSelectName>{team.name}</TeamSelectName>
                            <TeamSelectMeta>
                              {team.members?.filter(m => !m.isPending).length ||
                                team.memberCount ||
                                0}{' '}
                              members{team.domain ? ` · @${team.domain}` : ''}
                            </TeamSelectMeta>
                            {selectedTeamId === team.id &&
                              (() => {
                                const data = teams.find(t => t.id === team.id)
                                if (!data?.members?.length) return null
                                return (
                                  <TeamMemberPreview>
                                    {data.members
                                      .filter(m => m.user && !m.isPending)
                                      .slice(0, 4)
                                      .map((m, i) => (
                                        <TeamMemberChip key={i}>
                                          {m.user.firstName} {m.user.lastName}
                                        </TeamMemberChip>
                                      ))}
                                    {data.members.filter(
                                      m => m.user && !m.isPending
                                    ).length > 4 && (
                                      <TeamMemberMore>
                                        +
                                        {data.members.filter(
                                          m => m.user && !m.isPending
                                        ).length - 4}{' '}
                                        more
                                      </TeamMemberMore>
                                    )}
                                  </TeamMemberPreview>
                                )
                              })()}
                          </TeamSelectInfo>
                        </TeamSelectItem>
                      ))}
                  </>
                )}
              </TeamSelectList>
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setTeamModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalPrimaryBtn onClick={handleSaveTeam}>
                Save team
              </ModalPrimaryBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* External User Modal */}
      {externalModalOpen && (
        <ModalOverlay onClick={() => setExternalModalOpen(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Invite external user</ModalTitle>
              <ModalClose onClick={() => setExternalModalOpen(false)}>
                <CloseIcon />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Send an invitation email to someone outside your organisation.
              </ModalText>
              <InputGroup>
                <InputLabel>Email address *</InputLabel>
                <Input
                  type='email'
                  placeholder='email@example.com'
                  value={externalEmail}
                  onChange={e => setExternalEmail(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel>Name (optional)</InputLabel>
                <Input
                  type='text'
                  placeholder='John Smith'
                  value={externalName}
                  onChange={e => setExternalName(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel>Link expiry date (optional)</InputLabel>
                <Input
                  type='date'
                  value={externalExpiry}
                  onChange={e => setExternalExpiry(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                <InputHint>
                  After this date, the external user will no longer be able to
                  access the task.
                </InputHint>
              </InputGroup>
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setExternalModalOpen(false)}>
                Cancel
              </ModalCancelBtn>
              <ModalPrimaryBtn
                onClick={handleInviteExternal}
                disabled={!externalEmail.trim() || !externalEmail.includes('@')}
              >
                Send invitation
              </ModalPrimaryBtn>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      <TaskQrCodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        taskId={task?.id}
        taskTitle={task?.title}
        isRecurringSeries={!!task?.isRecurringSeries}
        isObligationTask={!!(task?.metadata?.programmeObligationId || task?.metadata?.programme_obligation_id)}
      />

      <TaskQrCodeModal
        open={!!extQr}
        onClose={() => setExtQr(null)}
        taskId={task?.id}
        externalAssigneeId={extQr?.id}
        taskTitle={extQr?.name || (extQr?.email ? maskEmail(extQr.email) : task?.title)}
        title='Share this task with a contractor'
        subCaption='Scanning opens a no-login view of this task for the external contractor.'
      />
    </PageWrapper>
  )
}

export default TaskDetail

// --- Animations ---

const saveSpin = keyframes`to { transform: rotate(360deg); }`
const SaveSpinner = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #f0c98a;
  border-top-color: #b8791e;
  animation: ${saveSpin} 0.7s linear infinite;
  display: inline-block;
  flex: 0 0 auto;
`
const SaveStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  ${({ $state }) =>
    $state === 'saving'
      ? 'background:#fef3c7; color:#92620a;'
      : $state === 'saved'
        ? 'background:#dcfce7; color:#15803d;'
        : 'background:#f2f4f7; color:#667085;'}
`
const ModalAddKeepBtn = styled.button`
  font: inherit;
  padding: 9px 16px;
  border-radius: 8px;
  border: 1px solid #2aa6b3;
  background: #fff;
  color: #1f8b98;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease;
  &:hover { background: #e9f6f7; }
`
const RolesSummary = styled.div`
  border: 1px solid #eceef2;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #fafbfc;
`
const RolesSummaryTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8a90a0;
  margin-bottom: 8px;
`
const RoleLine = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 3px 0;
`
const RoleTag = styled.span`
  flex: 0 0 82px;
  font-size: 12px;
  font-weight: 700;
  color: #55606f;
`
const RoleValue = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: ${({ $muted }) => ($muted ? '#a9adb7' : '#1c2029')};
`
const AssignSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid #e2e5ea;
  border-radius: 11px;
  margin-bottom: 10px;
`
const AssignSearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font: inherit;
  font-size: 13px;
  color: #1c2029;
  background: transparent;
`
const SelectAllRow = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 4px;
  font-size: 12.5px;
  font-weight: 700;
  color: #1f8b98;
`
const PeopleTickList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 320px;
  overflow-y: auto;
`
const PersonTickRow = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid ${({ $checked }) => ($checked ? '#bfe6ea' : 'transparent')};
  background: ${({ $checked }) => ($checked ? '#f2fbfb' : 'transparent')};
  &:hover { background: ${({ $checked }) => ($checked ? '#eaf8f9' : '#f7f8fa')}; }
`
const PeopleTick = styled.span`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #fff;
  border: 1.5px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  background: ${({ $on }) => ($on ? '#2aa6b3' : '#fff')};
`
const PersonTickInfo = styled.div`
  flex: 1;
  min-width: 0;
`
const PersonTickName = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #1c2029;
`
const PersonTickMeta = styled.div`
  font-size: 11.5px;
  color: #8a90a0;
  margin-top: 1px;
`
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`

// --- Styled Components ---

const PageWrapper = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  left: ${({ theme }) => theme.dimensions.navWidth};
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.backgroundColor};
  overflow-y: auto;
  overscroll-behavior-y: none;
  touch-action: pan-y;
  -webkit-overflow-scrolling: auto;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    left: 0;
    top: 60px;
    bottom: 0;
  }
`

const Header = styled.div`
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  position: sticky;
  top: 0;
  z-index: 100;
  transform: translateZ(0);
`

const HeaderContent = styled.div`
  box-sizing: border-box;
  max-width: 900px;
  margin: 0 auto;
  padding: 10px 24px 10px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;

  @media (max-width: 640px) {
    padding: 8px 16px;
  }
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`

const TrackerReturnBar = styled.button`
  font: inherit;
  display: block;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 10px 24px 0;
  box-sizing: border-box;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  color: #2aa6b3;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 640px) {
    padding: 8px 16px 0;
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }

  svg {
    font-size: 22px;
  }
`

const PageTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  flex: 1;
  min-width: 0;

  svg {
    font-size: 24px;
    color: ${({ theme }) => theme.colors.haiti};
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 640px) {
    font-size: 16px;

    svg {
      font-size: 20px;
    }
  }
`

const TitleText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${({ $editable }) => ($editable ? 'pointer' : 'default')};
  padding: 2px 4px;
  border-radius: 6px;
  transition: background 0.15s;

  > svg {
    font-size: 16px !important;
    opacity: 0;
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
    transition: opacity 0.15s;
    flex-shrink: 0;
  }

  ${({ $editable }) =>
    $editable &&
    `
    &:hover {
      background: rgba(0, 0, 0, 0.04);

      > svg {
        opacity: 1;
      }
    }
  `}
`

const TitleEditInput = styled.input`
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  padding: 4px 10px;
  outline: none;
  background: white;
  min-width: 0;
  box-shadow: 0 0 0 3px rgba(210, 71, 36, 0.15);

  @media (max-width: 640px) {
    font-size: 16px;
  }
`

const StatusChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-height: 40px;
    padding: 6px 12px;
    line-height: 1.2;
  }
`

const RejectionBanner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  padding: 16px 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  margin-top: 16px;
  box-sizing: border-box;
`

const RejectionBannerIcon = styled.div`
  color: #dc2626;
  svg {
    font-size: 22px;
  }
  flex-shrink: 0;
  margin-top: 1px;
`

const RejectionBannerContent = styled.div`
  flex: 1;
  min-width: 0;
`

const RejectionBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 4px;
`

const RejectionBannerReason = styled.div`
  font-size: 13px;
  color: #b91c1c;
  line-height: 1.5;
  white-space: pre-wrap;
`

const ContentArea = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
  padding-bottom: ${({ $hasActionBar }) => ($hasActionBar ? '120px' : '24px')};
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 640px) {
    padding: 16px;
    padding-bottom: ${({ $hasActionBar }) =>
      $hasActionBar
        ? 'calc(132px + env(safe-area-inset-bottom, 0px))'
        : '16px'};
    gap: 12px;
  }
`

const Card = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    /* On a phone the work comes first: whoever has to fill the form in should
       land on it, not scroll past overview, people and dependencies. The
       context cards stay below, in their usual order. */
    order: ${({ $mobileFirst }) => ($mobileFirst ? -1 : 0)};
  }

  @media (max-width: 640px) {
    padding: 16px;
    border-radius: 10px;
  }
`

const TaskGoneTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: #0c1229;
`

const TaskGoneText = styled.p`
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.5;
  color: #5f5f6a;
`

const TaskGoneButton = styled.button`
  min-height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #2aa6b3; }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const CardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }
`

const TaskDescription = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.6;
  margin: 0 0 20px 0;
  cursor: ${({ $editable }) => ($editable ? 'pointer' : 'default')};
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.15s;

  ${({ $editable }) =>
    $editable &&
    `
    &:hover {
      background: rgba(0, 0, 0, 0.03);
    }
  `}
`

const AddDescriptionBtn = styled.button`
  display: inline-block;
  padding: 8px 12px;
  margin-bottom: 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const DescriptionEditWrapper = styled.div`
  margin-bottom: 20px;
`

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  outline: none;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  box-shadow: 0 0 0 3px rgba(41, 152, 163, 0.1);
`

const DescriptionEditActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const InlineSaveBtn = styled.button`
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    opacity: 0.9;
  }
`

const InlineCancelBtn = styled.button`
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const ClickableMetaValue = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $danger, theme }) =>
    $danger ? theme.colors.primary : theme.colors.haiti};
  cursor: ${({ $editable }) => ($editable ? 'pointer' : 'default')};
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.15s;

  ${({ $editable }) =>
    $editable &&
    `
    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  `}
`

const InlineDateInput = styled.input`
  padding: 6px 10px;
  font-size: 13px;
  font-family: inherit;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 6px;
  outline: none;
  background: white;
  box-shadow: 0 0 0 3px rgba(41, 152, 163, 0.1);
`

const AddFieldBtn = styled.button`
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const ObligationActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`

const CustomiseObligationBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  font-size: 12.5px;
  font-weight: 700;
  color: #fff;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;

  svg { color: #fff; }
  &:hover { background: #2aa6b3; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const TaskKindBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  ${({ $kind }) => $kind === 'obligation'
    ? 'color: #0e7490; background: #e6f7f9;'
    : $kind === 'recurring'
      ? 'color: #6d28d9; background: #ede9fe;'
      : 'color: #475569; background: #eef2f6;'}
`

const OccurrencesPanel = styled.div`
  border: 1px solid #e5eef0;
  background: #fbfeff;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
`
const OccurrencesHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.haiti};
  svg { color: #0e7490; }
`
const OccurrencesCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e6f7f9;
  color: #0e7490;
  font-size: 11px;
  font-weight: 800;
`
const OccScheduleTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
`
const OccurrencesEmpty = styled.div`
  font-size: 12.5px;
  color: #8a90a0;
  padding: 8px 2px;
`
const OccurrencesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
  max-height: 240px;
  overflow-y: auto;
`
const OccurrenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 8px;
  cursor: ${({ $current }) => ($current ? 'default' : 'pointer')};
  background: ${({ $current }) => ($current ? '#e6f7f9' : 'transparent')};
  &:hover { background: ${({ $current }) => ($current ? '#e6f7f9' : '#f2f6f8')}; }
`
const OccDot = styled.span`
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $done, $overdue }) => ($overdue ? '#b00020' : $done ? '#15803d' : '#0e7490')};
`
const OccWhen = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const OccStatus = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  color: ${({ $overdue, $done }) => ($overdue ? '#b00020' : $done ? '#15803d' : '#64748b')};
`
const OccCurrentTag = styled.span`
  font-size: 10.5px;
  font-weight: 800;
  color: #0e7490;
  background: #cdeef2;
  border-radius: 999px;
  padding: 1px 7px;
`

const RecurrenceDetail = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-weight: 400;
`

const RecEditWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`

const RecEditRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
`

const RecInput = styled.input`
  font: inherit;
  font-size: 13px;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #111827;
  width: ${({ type }) => (type === 'number' ? '64px' : 'auto')};
  &:focus { outline: none; border-color: #33becc; }
`

const RecHint = styled.span`
  font-size: 12px;
  color: #9ca3af;
`

const RecActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
`

const RecLinkBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 7px;
  cursor: pointer;
  color: ${({ $primary }) => ($primary ? '#fff' : '#0f7a86')};
  background: ${({ $primary }) => ($primary ? '#33becc' : '#fff')};
  border: 1px solid ${({ $primary }) => ($primary ? '#33becc' : 'rgba(51,190,204,0.55)')};
  transition: background 0.15s, border-color 0.15s, filter 0.15s;
  &:hover { ${({ $primary }) => ($primary ? 'filter: brightness(1.05);' : 'background: rgba(51,190,204,0.08); border-color: #33becc;')} }
`

const StopRecurrenceBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  background: #fff;
  border: 1px solid #fca5a5;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #f87171;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ChildTaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const ChildTaskLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    text-decoration: underline;
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`

const ChildTaskStatus = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ $completed }) => ($completed ? '#059669' : '#d97706')};
  text-transform: capitalize;
  white-space: nowrap;
  flex-shrink: 0;
`

const DependencySectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  margin-top: 16px;
  margin-bottom: 8px;
`

const ComplianceObligationBanner = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  margin: 4px 0 16px;
  padding: 12px 14px;
  border: 1px solid #cdeef1;
  background: #f2fbfc;
  border-radius: 12px;
  transition: background 0.15s ease;

  &:hover { background: #e7f7f9; }
`

const CobIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #d3f0f3;
  color: #0e7490;
  flex-shrink: 0;
`

const CobText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const CobTitle = styled.span`
  font-size: 13.5px;
  color: #134e4a;
  strong { font-weight: 700; color: #0f766e; }
`

const CobSub = styled.span`
  font-size: 12px;
  color: #5b7a7d;
`

const ObligationLockNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.4;
  svg { color: #94a3b8; flex-shrink: 0; }
`

const CobChevron = styled.span`
  font-size: 22px;
  color: #0e7490;
  flex-shrink: 0;
  line-height: 1;
`

const ObligationEditWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin: 4px 0 16px;
  width: 100%;
`

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const MetaRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const MetaIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ $danger, theme }) =>
    $danger ? theme.colors.linen || '#fbede9' : theme.colors.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 18px;
    color: ${({ $danger, theme }) =>
      $danger ? theme.colors.primary : theme.colors.grey?.[800] || '#999'};
  }
`

const ChangeFormLink = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;

  &:hover { text-decoration: underline; }
`

const MetaContent = styled.div`
  flex: 1;
  min-width: 0;
`

const ApprovalState = styled.span`
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#15803d' : '#3d3d47')};
`

const ApprovalSwitch = styled.button`
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;
  width: 100%;
  padding: 11px 13px;
  border-radius: 12px;
  border: 1px solid ${({ $on }) => ($on ? '#33becc' : '#e6e8ee')};
  background: ${({ $on }) => ($on ? '#e6f7f9' : '#fff')};
  text-align: left;
  transition: border-color 140ms ease, background-color 140ms ease;
  &:hover { border-color: ${({ $on }) => ($on ? '#2aa6b3' : '#cbd5e1')}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const SwitchTrack = styled.span`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 23px;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? '#33becc' : '#cbd5e1')};
  transition: background-color 160ms ease;
`

const SwitchKnob = styled.span`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(12, 18, 41, 0.28);
  transition: transform 160ms ease;
  transform: translateX(${({ $on }) => ($on ? '17px' : '0')});
`


const OverlapWarn = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  color: #c2410c;
  background: #ffedd5;
  border: 1px solid #f5d0a9;
`

const ApprovalHint = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: #6b7280;
`

const MetaLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  margin-bottom: 2px;
`

const MetaValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $danger, theme }) =>
    $danger ? theme.colors.primary : theme.colors.haiti};
`

const MetaLink = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const PriorityChip = styled.span`
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $colour }) => $colour || '#475569'};
  background: ${({ $colour }) => `${$colour || '#64748b'}14`};
`

const DomainSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour || '#64748b'};
`

const ProgressSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
`

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
`

const ProgressPercent = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #059669;
`

const ProgressBar = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, #059669 0%, #10b981 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
`

const PeopleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PeopleRow = styled.div``

const PeopleLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  margin-bottom: 10px;
`

const PeopleLabelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const AssignToMeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.linen || '#fbede9'};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.linen || '#fbede9'};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const ManageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const PersonChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 6px;
  background: ${({ $external, $approver, theme }) =>
    $external ? '#fef3c7' : $approver ? '#e6f7ff' : theme.colors.lightGrey};
  border: 1px solid
    ${({ $external, $approver, theme }) =>
      $external
        ? '#fcd34d'
        : $approver
          ? theme.colors.secondary
          : theme.colors.mediumGrey};
  border-radius: 24px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const PersonChipSkeleton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 6px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 24px;
`


const PersonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const PersonEmail = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const RemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  margin-left: 4px;
  transition: all 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.15);
  }
`

const QrBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  margin-left: 4px;
  transition: all 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.15);
  }
`

const PendingPeople = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.haiti};
`

const PendingNote = styled.span`
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const EmptyText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;

  svg {
    font-size: 18px;
    color: #d97706;
    flex-shrink: 0;
  }
`

const DependencyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DependencyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ $completed }) => ($completed ? '#f0fdf4' : '#fffbeb')};
  border: 1px solid ${({ $completed }) => ($completed ? '#bbf7d0' : '#fde68a')};
  border-radius: 10px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: box-shadow 0.15s;

  ${({ $clickable }) =>
    $clickable &&
    `
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  `}
`

const DependencyIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $completed }) => ($completed ? '#d1fae5' : '#fef3c7')};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 18px;
    color: ${({ $completed }) => ($completed ? '#059669' : '#d97706')};
  }
`

const DependencyInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const DependencyTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #334155;
`

const DependencyStatus = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $completed }) => ($completed ? '#059669' : '#d97706')};
  text-transform: capitalize;
`

const CardHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ApproverEditToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${({ $active }) => ($active ? '#7c3aed' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#ede9fe' : 'white')};
  color: ${({ $active }) => ($active ? '#7c3aed' : '#6b7280')};

  &:hover {
    border-color: #7c3aed;
    color: #7c3aed;
    background: #f5f3ff;
  }
`

const ActivityCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  margin-left: 8px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  font-size: 11px;
  font-weight: 600;
`

const VersionHistoryToggle = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const ApproverEditBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #ede9fe;
  border: 1px solid #c4b5fd;
  border-radius: 8px;
  font-size: 13px;
  color: #5b21b6;

  svg {
    font-size: 18px;
    color: #7c3aed;
    flex-shrink: 0;
  }
`

const OnlineChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #059669;
`

const OnlineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
`

const OfflineChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #fef3c7;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
`

const ReminderNote = styled.textarea`
  width: 100%;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e0e0e6;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #4f7cff; }
`

const FormArea = styled.div``

const SubmissionLocBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  padding: 6px 12px;
  background: ${({ $bg }) => $bg};
  color: ${({ $tone }) => $tone};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
`

const CertWarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 14px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  color: #92400e;
`

const CertWarningBody = styled.div`
  font-size: 13px;
  line-height: 1.4;
  flex: 1;

  strong { font-weight: 600; }
`

const CertWarningList = styled.ul`
  margin: 6px 0 4px 18px;
  padding: 0;
  font-size: 12px;
  color: #78350f;

  li { margin-bottom: 2px; }
`

const CertWarningFoot = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: #b45309;
  font-style: italic;
`

const EmptyFormMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 14px;

  svg {
    font-size: 40px;
    color: #cbd5e1;
  }
`

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;

  @media (max-width: 640px) {
    position: fixed;
    bottom: env(safe-area-inset-bottom, 0px);
    left: 0;
    right: 0;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
    z-index: 1200;
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    transform: translateY(
      ${({ $mobileHidden }) => ($mobileHidden ? '110%' : '0')}
    );
    opacity: ${({ $mobileHidden }) => ($mobileHidden ? 0 : 1)};
    pointer-events: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'auto')};
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
  }
`

const ApproveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    box-shadow: 0 4px 12px rgba(41, 152, 163, 0.3);
  }
`

const RejectBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.linen || '#fbede9'};
  }
`

const HeaderApproveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 40px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  svg { font-size: 16px; }

  &:hover {
    background: #2aa6b3;
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const HeaderSubmitBtn = styled(HeaderApproveBtn)``

const OpenBpButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 40px;
  padding: 6px 12px;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    font-size: 16px;
    color: #1f8b98;
  }

  &:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    max-width: 170px;
    line-height: 1.2;
    white-space: normal;
    text-align: left;
  }
`

const NarrowPrimaryRow = styled.div`
  display: flex;

  > button {
    flex: 1 1 auto;
    justify-content: center;
  }
`

const NarrowMetaRow = styled.div`
  box-sizing: border-box;
  max-width: 900px;
  margin: 0 auto;
  padding: 12px 16px 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const ReviewInfoText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #7c3aed;
  font-weight: 500;
  width: 100%;

  svg {
    color: #7c3aed;
  }
`

const ReplicateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.haiti};
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    font-size: 20px;
  }

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(31, 41, 55, 0.3);
  }
`

const DownloadReportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    font-size: 20px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.secondary};
    color: white;
    box-shadow: 0 4px 12px rgba(41, 152, 163, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

// --- Modal Styles ---

const ModalOverlayBase = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  z-index: 9999;
  padding: 20px;
`

const ModalOverlay = ({ children, ...props }) =>
  createPortal(<ModalOverlayBase {...props}>{children}</ModalOverlayBase>, document.body)

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: ${({ $wide }) => ($wide ? '560px' : '440px')};
  max-height: calc(100vh - 40px);
  margin: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.2s ease-out;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const ModalClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    color: ${({ theme }) => theme.colors.haiti};
  }

  svg {
    font-size: 20px;
  }
`

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: ${({ $tall }) => ($tall ? 'min(320px, 50vh)' : '0')};
`

const ModalText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px 0;
  line-height: 1.5;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  flex-wrap: wrap;

  @media (max-width: 480px) {
    & > button { flex: 1; min-width: 130px; }
  }
`

const ModalCancelBtn = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }
`

const ModalPrimaryBtn = styled.button`
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.haiti};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ModalDangerBtn = styled(ModalPrimaryBtn)`
  background: #dc2626;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }
`

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
  outline: none;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(41, 152, 163, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const ModalInput = styled.input`
  width: 100%;
  padding: 10px 16px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(41, 152, 163, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const HoldBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 24px;
  background: #fef3c7;
  border-bottom: 1px solid #fcd34d;

  @media (max-width: 640px) {
    padding: 12px 16px;
  }
`

const HoldBannerIcon = styled.div`
  color: #d97706;
  svg {
    font-size: 22px;
  }
  flex-shrink: 0;
  margin-top: 1px;
`

const HoldBannerContent = styled.div`
  flex: 1;
  min-width: 0;
`

const HoldBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
`

const HoldBannerReason = styled.div`
  font-size: 13px;
  color: #92400e;
  margin-top: 4px;
`

const HoldBannerNextAction = styled.div`
  font-size: 13px;
  color: #b45309;
  margin-top: 4px;
  font-style: italic;
`

const HoldBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: #d97706;
  border: 1px solid #d97706;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: #fef3c7;
  }
`

const ResumeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: #047857;
  }
`

const SignOffBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 24px;
  background: ${({ $signedOff }) => ($signedOff ? '#ecfdf5' : '#fffbeb')};
  border: 1px solid ${({ $signedOff }) => ($signedOff ? '#a7f3d0' : '#fde68a')};
  border-radius: 12px;
  margin: 0 24px;

  @media (max-width: 640px) {
    margin: 0 16px;
    padding: 12px 16px;
  }
`

const SignOffBannerIcon = styled.div`
  color: ${({ $signedOff }) => ($signedOff ? '#059669' : '#d97706')};
  svg {
    font-size: 22px;
  }
  flex-shrink: 0;
  margin-top: 1px;
`

const SignOffBannerContent = styled.div`
  flex: 1;
  min-width: 0;
`

const SignOffBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`

const SignOffBannerDetail = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;

  strong {
    color: #334155;
  }
`

const SignOffBannerNotes = styled.div`
  font-size: 13px;
  color: #475569;
  margin-top: 4px;
  font-style: italic;
`

const SignOffBannerSignature = styled.div`
  margin-top: 8px;

  img {
    max-width: 240px;
    max-height: 80px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    padding: 4px;
  }
`

const SignOffSigLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`

const SignOffSigCanvasWrapper = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #fafafa;
  overflow: hidden;

  canvas {
    display: block;
    cursor: crosshair;
  }
`

const SignOffSigActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  margin-bottom: 12px;
`

const SignOffSigClearBtn = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 4px;

  &:hover {
    text-decoration: underline;
  }
`

const SignOffSigHint = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
`

const SignOffBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: #047857;
  }
`

const InputGroup = styled.div`
  margin-bottom: 16px;
`

const InputLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  margin-bottom: 6px;
`

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(41, 152, 163, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const InputHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
  margin-top: 4px;
`

const BlockerList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
`

const BlockerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.linen || '#fbede9' : 'white'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.linen || '#fbede9' : theme.colors.lightGrey};
  }
`

const BlockerCheckbox = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.secondary : theme.colors.mediumGrey};
  background: ${({ $checked, theme }) =>
    $checked ? theme.colors.secondary : 'white'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  svg {
    font-size: 16px;
  }
`

const BlockerContent = styled.div`
  flex: 1;
  min-width: 0;
`

const BlockerTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BlockerMeta = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`

// --- Team Styles ---

const TeamChangeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #0d7377;
  background: transparent;
  border: 1px solid #b2dfdb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  vertical-align: middle;

  &:hover {
    background: #e0f7f7;
    border-color: #0d7377;
  }
`

const TeamShareBanner = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #e0f7f7;
  border: 1px solid #b2dfdb;
  border-radius: 10px;
  margin-bottom: 8px;
`

const TeamShareIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;

  svg {
    font-size: 20px;
    color: #0d7377;
  }
`

const TeamShareContent = styled.div`
  flex: 1;
  min-width: 0;
`

const TeamShareSection = styled.div`
  &:not(:first-child) {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid
      ${({ $external }) => ($external ? '#fde68a' : '#b2dfdb')};
  }
`

const TeamShareText = styled.div`
  font-size: 13px;
  color: ${({ $external }) => ($external ? '#92400e' : '#0d7377')};
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`

const TeamExternalList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`

const TeamExternalItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: white;
  border: 1px solid #fde68a;
  border-radius: 16px;
`

const TeamExternalEmail = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #78350f;
  white-space: nowrap;
`

const TeamExternalInviteBtn = styled.button`
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #d97706;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: #d97706;
    color: white;
    border-color: #d97706;
  }
`

const TeamExternalInvited = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #059669;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 3px 8px;
`

const AddTeamMembersBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #0d7377;
  background: white;
  border: 1px solid #0d7377;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #0d7377;
    color: white;
  }
`

const TeamSelectList = styled.div`
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
`

const TeamSelectItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#e0f7f7' : 'white')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $selected }) => ($selected ? '#e0f7f7' : '#f8fafa')};
  }
`

const TeamSelectRadio = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${({ $checked }) => ($checked ? '#0d7377' : '#d1d5db')};
  background: ${({ $checked }) => ($checked ? '#0d7377' : 'white')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.15s;

  svg {
    font-size: 16px;
  }
`

const TeamSelectInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const TeamSectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 4px;
`

const TeamSectionDividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e2e8f0;
`

const TeamSectionDividerText = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  white-space: nowrap;
`

const TeamSelectName = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
`

const TeamSelectMeta = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`

const TeamMemberPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`

const TeamMemberChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: white;
  border: 1px solid #b2dfdb;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: #1a3636;
`

const TeamMemberMore = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  color: #0d7377;
  padding: 3px 8px;
`

const TeamMemberList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`

const TeamMemberItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: white;
  border: 1px solid #b2dfdb;
  border-radius: 16px;
`

const TeamMemberName = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #1a3636;
  white-space: nowrap;
`

const TeamMemberAddBtnInternal = styled.button`
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #0d7377;
  background: #e0f7f7;
  border: 1px solid #b2dfdb;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: #0d7377;
    color: white;
    border-color: #0d7377;
  }
`

const TeamMemberAssigned = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #059669;
  white-space: nowrap;
  flex-shrink: 0;
`
